type ModelSettingsTab = 'default' | 'audio' | 'image' | 'video';
import { Component, Input, Output, EventEmitter, ViewChild, ViewChildren, QueryList, ElementRef, AfterViewInit, OnInit, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { OpenRouterProviderRecommendation } from '@platfio/interfaces/platfio';
import { AgentOption, AttachedContextItem, ChatDeviceCommandTarget, ChatDisplayMode, ChatFileAttachment, ChatImageAttachment, ChatKeyboardEvent, ChatThreadAssetItem, ChatThreadAssetKind, ChatThreadViewAction, ChatThreadViewOption, InputChangeEvent, ModelOption, ToolOption } from '../interfaces/chat-component.interfaces';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { PlaybookEntry, PlaybookField, getPlaybookBySlug, getPlaybooksForAgent, resolvePlaybookPrompt } from 'src/app/data/playbook-catalog';
import { PlaybookSubmission } from './prompt-popover/prompt-popover.component';
import { UiModalService } from 'src/app/components/elements/ui-modal/ui-modal.service';
import { UiPopoverService } from 'src/app/components/elements/ui-popover/ui-popover.service';
import { UiTooltipService } from 'src/app/components/elements/ui-tooltip/ui-tooltip.service';
import { AttachedContextService } from '../attached-context.service';
import { ActionsPaletteService } from 'src/app/components/layout/actions-palette/actions-palette.service';
import { AuthService } from 'src/app/services/auth.service';
import { ThemeService } from 'src/app/services/theme.service';
import { ToastService } from 'src/app/services/toast.service';
import { Subscription } from 'rxjs';
import { agentSelectorOptions, defaultAgentSlug, getAgentName } from 'src/app/data/agent-catalog';
import { areToolSelectionsEqual, normalizeToolNameList } from 'src/app/data/agent-tool-defaults';

const SUPPORTED_IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const MAX_IMAGE_ATTACHMENTS = 4;
const MAX_IMAGE_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const MAX_CONTEXT_FILE_BYTES = 25 * 1024 * 1024;

interface ModelGroupEntry {
  model: ModelOption;
  tierKey?: CuratedTierKey;
  tier: string;
  summary: string;
  badgeStyle: Record<string, string>;
  avatar: string;
  tooltipText: string;
  secondaryText: string;
  pricingText: string;
  compactPricingText: string;
  badges: string[];
  selectable: boolean;
}

interface ModelGroup {
  key: string;
  label: string;
  description: string;
  count: number;
  recommendedEntries: ModelGroupEntry[];
  missingTiers: Array<{ tier: string; reason: string }>;
  additionalEntries: ModelGroupEntry[];
  badgeStyle: Record<string, string>;
  avatar: string;
}

interface ToolGroup {
  key: string;
  label: string;
  description: string;
  tools: ToolOption[];
  selectedCount: number;
  order: number;
}

interface AgencySkillSummary {
  id?: string;
  name: string;
  description: string;
  files?: { path: string; content?: string }[];
}

interface ComposerSkillTokenPart {
  type: 'text' | 'skill';
  value: string;
}

interface ComposerSkillTokenRange {
  name: string;
  start: number;
  end: number;
}

type CuratedTierKey = 'good' | 'better' | 'best';

interface ProviderTierDefinition {
  key: CuratedTierKey;
  label: string;
  summary: string;
  includePatterns: RegExp[];
  excludePatterns?: RegExp[];
  preferPatterns?: RegExp[];
  versionPatterns?: RegExp[];
  allowPreview?: boolean;
  allowFallback?: boolean;
}

interface ProviderModelDefinition {
  key: string;
  label: string;
  description: string;
  includeInRecommended?: boolean;
  tiers: ProviderTierDefinition[];
}

interface RecommendedModelEntry {
  providerKey: string;
  providerLabel: string;
  providerDescription: string;
  model: ModelOption;
  tierKey: CuratedTierKey;
  tier: string;
  summary: string;
  blendedPrice?: number;
  badgeStyle: Record<string, string>;
  avatar: string;
  tooltipText: string;
  secondaryText: string;
  pricingText: string;
  badges: string[];
  selectable: boolean;
  meta: string;
}

type RecommendedGroupingMode = 'provider' | 'all';
type RecommendedSortDirection = 'asc' | 'desc';
type SelectorModalKind = 'add' | 'more' | 'agent' | 'mode' | 'model' | 'tools' | 'prompts' | 'context' | 'details' | 'contextUsage' | 'view' | 'assets';
type ComposerSuggestionsPlacement = 'above' | 'below' | 'hidden';

interface RecommendedGroupingOption {
  value: RecommendedGroupingMode;
  label: string;
}

type PriceBlendPresetKey = 'rag' | 'balanced' | 'verbose';

interface PriceBlendPreset {
  key: PriceBlendPresetKey;
  label: string;
  ratioLabel: string;
  inputShare: number;
}

interface ComposerAttachmentListItem {
  key: string;
  type: 'context' | 'image' | 'file';
  label: string;
  iconClass: string;
  status: 'ready' | 'uploading' | 'error';
  item: AttachedContextItem | ChatImageAttachment | ChatFileAttachment;
}

type ContextAttachmentSource = 'business' | 'partner' | 'funnel';
type AddMenuAction = 'image' | 'file' | 'context-business' | 'context-partner' | 'context-funnel';
type MoreMenuAction = 'playbooks' | 'mode' | 'credits' | 'history' | 'connectors' | 'devices' | 'skills' | 'tools' | 'view' | 'assets' | 'details';
type ThreadAssetFilter = 'all' | 'preview' | 'file' | 'image' | 'audio';

interface ThreadAssetFilterOption {
  value: ThreadAssetFilter;
  label: string;
}

interface MoreMenuOption {
  value: MoreMenuAction;
  label: string;
  description: string;
  iconClass: string;
  badgeText?: string;
  disabled?: boolean;
}

interface AddMenuOption {
  value: AddMenuAction;
  label: string;
  description: string;
  iconClass: string;
}

interface DisplayModeOption {
  id: ChatDisplayMode;
  label: string;
  description: string;
  icon: string;
}

interface InlinePreviewModeOption {
  id: 'inline-previews-off' | 'inline-previews-on';
  label: string;
  description: string;
  icon: string;
  enabled: boolean;
}

interface SelectorModalShortcutHint {
  label: string;
  ariaLabel: string;
}

interface SelectorModalHeaderContent {
  title: string;
  subtitle?: string;
  shortcutHint?: SelectorModalShortcutHint;
}

const TOOL_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  dataGetTypesSummary: 'List App Data Types',
  dataGetTypeDetail: 'Inspect App Data Type',
  dataGet: 'Read App Data',
  dataSet: 'Write App Data',
  dataIdGenerate: 'Generate App Data ID',
  getAffiliate: 'Get Partner',
  setAffiliate: 'Set Partner',
  deleteAffiliate: 'Delete Partner',
  getFirebase: 'Get Firebase Configuration',
  render_preview: 'Render Preview',
  initialize_computer: 'Initialize Computer',
  read_artifact: 'Read Artifact',
  run_terminal_command: 'Run Terminal Command',
  save_and_pause_computer: 'Pause Computer',
  setPlanDrawflowBoard: 'Set Plan Board',
  removePlanDrawflowBoard: 'Remove Plan Board'
};

const SELECTOR_MODAL_HEADER_CONTENT: Partial<Record<SelectorModalKind, SelectorModalHeaderContent>> = {
  add: {
    title: 'Add attachment',
    subtitle: 'Attach an image, upload a file, or add agency context.',
    shortcutHint: {
      label: '⌥⇧+',
      ariaLabel: 'Open with Option Shift Plus'
    }
  },
  more: {
    title: 'More options',
    shortcutHint: {
      label: '⌥⇧O',
      ariaLabel: 'Open with Option Shift O'
    }
  },
  agent: {
    title: 'Choose Agent',
    shortcutHint: {
      label: '@',
      ariaLabel: 'Open by typing at sign'
    }
  },
  mode: {
    title: 'Choose mode',
    subtitle: 'Ask for a single response or let the agent run multi-step work.',
    shortcutHint: {
      label: '+',
      ariaLabel: 'Open by typing plus'
    }
  },
  model: {
    title: 'Models',
    shortcutHint: {
      label: '⌥⇧M',
      ariaLabel: 'Open with Option Shift M'
    }
  },
  tools: {
    title: 'Tools',
    subtitle: 'Restrict the tool catalog for this thread. Leaving everything unselected keeps all tools available.',
    shortcutHint: {
      label: '⌥⇧T',
      ariaLabel: 'Open with Option Shift T'
    }
  },
  prompts: {
    title: 'Playbooks',
    subtitle: 'Run a structured playbook for the current agent.'
  },
  context: {
    title: 'Attach context',
    subtitle: 'Swap between businesses, partners, and funnels, then attach the exact context you want in the thread.',
    shortcutHint: {
      label: '#',
      ariaLabel: 'Open by typing hash'
    }
  },
  details: {
    title: 'Thread details',
    subtitle: 'Inspect credits, token and compute breakdowns, rename the thread, and manage deletion.',
    shortcutHint: {
      label: '?',
      ariaLabel: 'Open by typing question mark'
    }
  },
  contextUsage: {
    title: 'Context window',
    subtitle: 'Inspect prompt token usage and compact older history when the thread is near the limit.'
  },
  view: {
    title: 'Change view',
    subtitle: 'Choose the thread layout and how much message detail to show in this conversation.'
  }
};

const DISPLAY_MODE_OPTIONS: DisplayModeOption[] = [
  {
    id: 'minimal',
    label: 'Minimal',
    description: 'Reduce the thread down to the essentials.',
    icon: 'fa-regular fa-message'
  },
  {
    id: 'standard',
    label: 'Standard',
    description: 'Keep the normal message layout and metadata.',
    icon: 'fa-regular fa-rectangle-list'
  },
  {
    id: 'debug',
    label: 'Debug',
    description: 'Show the additional diagnostics while you troubleshoot.',
    icon: 'fa-solid fa-bug'
  }
];

const INLINE_PREVIEW_MODE_OPTIONS: InlinePreviewModeOption[] = [
  {
    id: 'inline-previews-off',
    label: 'Off',
    description: 'Keep previews and files behind the side panel button.',
    icon: 'fa-regular fa-eye-slash',
    enabled: false
  },
  {
    id: 'inline-previews-on',
    label: 'On',
    description: 'Show preview and file surfaces in the thread when the side panel is closed.',
    icon: 'fa-regular fa-rectangle-history',
    enabled: true
  }
];

const ADD_MENU_OPTIONS: AddMenuOption[] = [
  {
    value: 'image',
    label: 'Attach image',
    description: 'Upload screenshots, mocks, or photos for multimodal models.',
    iconClass: 'fa-regular fa-image'
  },
  {
    value: 'file',
    label: 'Attach file',
    description: 'Upload a document or asset into the composer context lane.',
    iconClass: 'fa-regular fa-file'
  },
  {
    value: 'context-business',
    label: 'Attach agency businesses',
    description: 'Open the business context picker and attach a business or its related work.',
    iconClass: 'fa-regular fa-building'
  },
  {
    value: 'context-partner',
    label: 'Attach agency partners',
    description: 'Jump straight into partner references and attach the relevant partner.',
    iconClass: 'fa-regular fa-handshake'
  },
  {
    value: 'context-funnel',
    label: 'Attach agency funnels',
    description: 'Open funnels directly so you can attach the funnel, pages, or forms.',
    iconClass: 'fa-regular fa-filter'
  }
];

const THREAD_ASSET_FILTER_DEFINITIONS: Array<ThreadAssetFilterOption & { kinds: ChatThreadAssetKind[] }> = [
  { value: 'all', label: 'All', kinds: ['tool-preview', 'artifact', 'generated-image', 'generated-audio'] },
  { value: 'preview', label: 'Preview', kinds: ['tool-preview'] },
  { value: 'image', label: 'Image', kinds: ['generated-image'] },
  { value: 'audio', label: 'Audio', kinds: ['generated-audio'] },
  { value: 'file', label: 'File', kinds: ['artifact'] }
];

const OPENAI_AUDIO_VOICE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'alloy', label: 'Alloy' },
  { value: 'echo', label: 'Echo' },
  { value: 'fable', label: 'Fable' },
  { value: 'nova', label: 'Nova' },
  { value: 'onyx', label: 'Onyx' },
  { value: 'shimmer', label: 'Shimmer' }
];

@Component({
  selector: 'app-input-container',
  templateUrl: './input-container.component.html',
  styleUrl: './input-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputContainerComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private readonly providerDefinitions: ProviderModelDefinition[] = [
    {
      key: 'openai',
      label: 'OpenAI',
      description: 'GPT-5.4 lineup organized as nano, mini, and the standard flagship model.',
      tiers: [
        {
          key: 'good',
          label: 'Good',
          summary: 'Haiku-style: fast + cheap',
          includePatterns: [/nano/i],
          excludePatterns: [/mini/i, /pro/i, /^openai\/o\d/i],
          preferPatterns: [/gpt-5\.4-nano/i, /gpt-5-nano/i],
          versionPatterns: [/gpt-(\d+(?:\.\d+)?)-nano/i]
        },
        {
          key: 'better',
          label: 'Better',
          summary: 'Sonnet-style: balanced',
          includePatterns: [/mini/i],
          excludePatterns: [/nano/i, /pro/i, /^openai\/o\d/i],
          preferPatterns: [/gpt-5\.4-mini/i, /gpt-5-mini/i],
          versionPatterns: [/gpt-(\d+(?:\.\d+)?)-mini/i]
        },
        {
          key: 'best',
          label: 'Best',
          summary: 'Opus-style: max power',
          includePatterns: [/gpt-5/i],
          excludePatterns: [/nano/i, /mini/i, /pro/i, /^openai\/o\d/i],
          preferPatterns: [/gpt-5\.4$/i, /gpt-5$/i],
          versionPatterns: [/gpt-(\d+(?:\.\d+)?)(?=$|[^a-z0-9])/i]
        }
      ]
    },
    {
      key: 'anthropic',
      label: 'Anthropic',
      description: 'The reference ladder: Haiku for speed, Sonnet for balance, Opus for depth.',
      tiers: [
        {
          key: 'good',
          label: 'Good',
          summary: 'Haiku-style: fast + cheap',
          includePatterns: [/haiku/i],
          preferPatterns: [/claude-haiku/i],
          versionPatterns: [/claude-haiku-(\d+(?:\.\d+)?)/i]
        },
        {
          key: 'better',
          label: 'Better',
          summary: 'Sonnet-style: balanced',
          includePatterns: [/sonnet/i],
          preferPatterns: [/claude-sonnet/i],
          versionPatterns: [/claude-sonnet-(\d+(?:\.\d+)?)/i]
        },
        {
          key: 'best',
          label: 'Best',
          summary: 'Opus-style: max power',
          includePatterns: [/opus/i],
          excludePatterns: [/fast/i],
          preferPatterns: [/claude-opus/i],
          versionPatterns: [/claude-opus-(\d+(?:\.\d+)?)/i],
          allowFallback: false
        }
      ]
    },
    {
      key: 'google',
      label: 'Google',
      description: 'Gemini 3.1 lineup: Flash Lite for speed, Pro for balance, and Pro preview for the top tier.',
      tiers: [
        {
          key: 'good',
          label: 'Good',
          summary: 'Haiku-style: fast + cheap',
          includePatterns: [/flash-lite/i],
          excludePatterns: [/pro/i, /thinking/i],
          preferPatterns: [/gemini-3\.1-flash-lite/i, /gemini-3-flash-lite/i, /gemini-2\.5-flash-lite/i],
          versionPatterns: [/gemini-(\d+(?:\.\d+)?)-flash-lite/i],
          allowPreview: true
        },
        {
          key: 'better',
          label: 'Better',
          summary: 'Sonnet-style: balanced',
          includePatterns: [/flash/i],
          excludePatterns: [/flash-lite/i, /thinking/i],
          preferPatterns: [/gemini-3-flash/i, /gemini-2\.5-flash/i],
          versionPatterns: [/gemini-(\d+(?:\.\d+)?)-flash/i],
          allowPreview: true
        },
        {
          key: 'best',
          label: 'Best',
          summary: 'Opus-style: max power',
          includePatterns: [/pro/i],
          excludePatterns: [/thinking/i],
          preferPatterns: [/gemini-3\.1-pro/i, /gemini-3-pro/i],
          versionPatterns: [/gemini-(\d+(?:\.\d+)?)-pro/i],
          allowPreview: true
        }
      ]
    },
    {
      key: 'x-ai',
      label: 'xAI',
      description: 'Grok organized into fast, balanced, and max-power lanes.',
      tiers: [
        {
          key: 'good',
          label: 'Good',
          summary: 'Haiku-style: fast + cheap',
          includePatterns: [/fast/i],
          excludePatterns: [/grok-3/i],
          preferPatterns: [/grok-4\.1-fast/i, /grok-4-fast/i],
          versionPatterns: [/grok-(\d+(?:\.\d+)?)/i]
        },
        {
          key: 'better',
          label: 'Better',
          summary: 'Sonnet-style: balanced',
          includePatterns: [/grok/i],
          excludePatterns: [/fast/i, /mini/i, /agent/i, /reason/i, /grok-3/i],
          preferPatterns: [/grok-4\.20/i, /grok-4/i, /grok-5/i],
          versionPatterns: [/grok-(\d+(?:\.\d+)?)/i]
        },
        {
          key: 'best',
          label: 'Best',
          summary: 'Opus-style: max power',
          includePatterns: [/agent/i, /reason/i],
          excludePatterns: [/grok-3/i],
          preferPatterns: [/grok-4\.20-multi-agent/i, /multi-agent/i, /agent/i, /grok-4/i, /grok-5/i],
          versionPatterns: [/grok-(\d+(?:\.\d+)?)/i]
        }
      ]
    },
    {
      key: 'deepseek',
      label: 'DeepSeek',
      description: 'DeepSeek V3.2 is the default general-purpose recommendation; adjacent variants stay available in the full catalog.',
      tiers: [
        {
          key: 'good',
          label: 'Recommended',
          summary: 'Default DeepSeek pick',
          includePatterns: [/deepseek-v\d(?:\.\d+)?/i],
          excludePatterns: [/chat/i, /exp/i, /speciale/i, /r1/i, /reason/i, /think/i],
          preferPatterns: [/v3\.2/i],
          versionPatterns: [/\bv(\d+(?:\.\d+)*)\b/i]
        }
      ]
    },
    {
      key: 'moonshotai',
      label: 'Moonshot',
      description: 'Kimi K2.5 is the default Moonshot recommendation, with K2 Thinking as the reasoning option.',
      tiers: [
        {
          key: 'good',
          label: 'Recommended',
          summary: 'Default Kimi pick',
          includePatterns: [/kimi-k2\.5/i],
          preferPatterns: [/kimi-k2\.5/i],
          versionPatterns: [/kimi-k(\d+(?:\.\d+)?)/i]
        },
        {
          key: 'best',
          label: 'Thinking',
          summary: 'Reasoning-focused Kimi',
          includePatterns: [/think/i, /reason/i],
          preferPatterns: [/kimi-k2-thinking/i, /think/i, /reason/i],
          versionPatterns: [/kimi-k(\d+(?:\.\d+)?)/i]
        }
      ]
    },
    {
      key: 'meta-llama',
      label: 'Meta',
      description: 'Llama organized into light, balanced, and flagship picks.',
      includeInRecommended: false,
      tiers: [
        {
          key: 'good',
          label: 'Good',
          summary: 'Fast + cheap',
          includePatterns: [/scout/i, /mini/i, /small/i, /8b/i, /3b/i],
          preferPatterns: [/llama-4/i, /llama-3/i]
        },
        {
          key: 'better',
          label: 'Better',
          summary: 'Balanced',
          includePatterns: [/llama/i],
          excludePatterns: [/scout/i, /maverick/i, /small/i, /mini/i, /8b/i, /3b/i],
          preferPatterns: [/llama-4/i, /llama-3/i]
        },
        {
          key: 'best',
          label: 'Best',
          summary: 'Max power',
          includePatterns: [/maverick/i, /70b/i, /405b/i],
          preferPatterns: [/llama-4/i, /maverick/i]
        }
      ]
    },
    {
      key: 'mistralai',
      label: 'Mistral',
      description: 'Ministral, medium, and large tiers mapped to good, better, and best.',
      includeInRecommended: false,
      tiers: [
        {
          key: 'good',
          label: 'Good',
          summary: 'Fast + cheap',
          includePatterns: [/ministral/i, /small/i, /8b/i, /3b/i],
          preferPatterns: [/mistral/i]
        },
        {
          key: 'better',
          label: 'Better',
          summary: 'Balanced',
          includePatterns: [/medium/i],
          preferPatterns: [/mistral/i]
        },
        {
          key: 'best',
          label: 'Best',
          summary: 'Max power',
          includePatterns: [/large/i],
          preferPatterns: [/mistral/i]
        }
      ]
    },
    {
      key: 'qwen',
      label: 'Qwen',
      description: 'Qwen favors 9B, 27B-class, and Plus tiers for general-purpose text picks.',
      tiers: [
        {
          key: 'good',
          label: 'Good',
          summary: 'Haiku-style: fast + cheap',
          includePatterns: [/\b9b\b/i],
          excludePatterns: [/coder/i, /vl/i, /vision/i, /max/i, /plus/i, /reason/i, /think/i],
          preferPatterns: [/qwen3\.5-9b/i, /qwen3-9b/i],
          versionPatterns: [/qwen(\d+(?:\.\d+)?)/i]
        },
        {
          key: 'better',
          label: 'Better',
          summary: 'Sonnet-style: balanced',
          includePatterns: [/\b2?7b\b/i, /\b32b\b/i],
          excludePatterns: [/coder/i, /vl/i, /vision/i, /max/i, /plus/i, /reason/i, /think/i],
          preferPatterns: [/27b/i],
          versionPatterns: [/qwen(\d+(?:\.\d+)?)/i]
        },
        {
          key: 'best',
          label: 'Best',
          summary: 'Opus-style: max power',
          includePatterns: [/plus/i],
          excludePatterns: [/coder/i, /vl/i, /vision/i, /max/i, /reason/i, /think/i],
          preferPatterns: [/qwen3\.6-plus/i, /qwen3\.5-plus/i, /plus/i],
          versionPatterns: [/qwen(\d+(?:\.\d+)?)/i]
        }
      ]
    }
  ];

  providerExpansionState: Record<string, boolean> = {};
  groupedAvailableModels: ModelGroup[] = [];
  visibleGroupedAvailableModels: ModelGroup[] = [];
  groupedAvailableTools: ToolGroup[] = [];
  toolGroupExpansionState: Record<string, boolean> = {};
  recommendedModelEntries: RecommendedModelEntry[] = [];
  sortedRecommendedModelEntries: RecommendedModelEntry[] = [];
  recommendedGroupingMode: RecommendedGroupingMode = 'all';
  recommendedSortDirection: RecommendedSortDirection = 'asc';
  priceBlendPresetKey: PriceBlendPresetKey = 'balanced';
  isPriceBlendSelectorOpen = false;

  readonly recommendedGroupingOptions: RecommendedGroupingOption[] = [
    { value: 'all', label: 'All' },
    { value: 'provider', label: 'By provider' }
  ];

  readonly priceBlendPresets: PriceBlendPreset[] = [
    { key: 'rag', label: 'RAG', ratioLabel: '80/20', inputShare: 0.8 },
    { key: 'balanced', label: 'Balanced', ratioLabel: '70/30', inputShare: 0.7 },
    { key: 'verbose', label: 'Verbose', ratioLabel: '50/50', inputShare: 0.5 }
  ];

  readonly modelSettingsTabs: Array<{ key: ModelSettingsTab; label: string }> = [
    { key: 'default', label: 'Default' },
    { key: 'audio', label: 'Audio' },
    { key: 'image', label: 'Image' },
    { key: 'video', label: 'Video' }
  ];

  @Input() isExpanded: boolean = false;
  @Input() newMessage: string = '';
  @Input() loading: boolean = false;
  @Input() initializingThread: boolean = false; // For thread creation loading
  @Input() isRecording: boolean = false;
  @Input() isConnecting: boolean = false; // New connecting state for immediate feedback
  @Input() autofocusOnLoad: boolean = false;
  @Input() agent: string = defaultAgentSlug;
  @Input() messageCount: number = 0;
  @Input() hasAttachedContext: boolean = false;
  @Input() attachedItems: AttachedContextItem[] = [];
  @Input() attachedImageItems: ChatImageAttachment[] = [];
  @Input() attachedFileItems: ChatFileAttachment[] = [];
  @Input() imageFilesSelectedHandler?: (files: File[]) => void;
  @Input() filesSelectedHandler?: (files: File[]) => void;
  @Input() isProcessingTranscription: boolean = false;
  @Input() showTranscriptionCancel: boolean = false;
  @Input() currentMode: 'ask' | 'agent' = 'agent';
  @Input() currentModelLabel: string = '';
  @Input() currentModelKey: string = '';
  @Input() availableTools: ToolOption[] = [];
  @Input() selectedToolNames: string[] = [];
  @Input() defaultToolNames: string[] = [];
  @Input() currentAudioModelKey: string = '';
  @Input() currentImageModelKey: string = '';
  @Input() currentVideoModelKey: string = '';
  @Input() currentAudioVoice: string = '';
  @Input() imageAttachmentsSupported: boolean = true;
  @Input() imageAttachmentValidationMessage: string = '';
  @Input() availableModels: ModelOption[] = [];
  @Input() availableVideoModels: ModelOption[] = [];
  @Input() defaultAudioModelKey: string = '';
  @Input() defaultAudioVoice: string = '';
  @Input() defaultImageModelKey: string = '';
  @Input() defaultVideoModelKey: string = '';
  @Input() audioVoiceOptionsByModel: Record<string, Array<{ value: string; label: string }>> = {};
  @Input() providerRecommendations: OpenRouterProviderRecommendation[] = [];
  @Input() modelCatalogStatus: 'loading' | 'live' | 'error' = 'loading';
  @Input() modelCatalogSourceLabel: string = 'Loading model catalog';
  @Input() modelCatalogMessage: string = '';
  @Input() toolCatalogStatus: 'loading' | 'live' | 'error' = 'loading';
  @Input() toolCatalogSourceLabel: string = 'Loading tool catalog';
  @Input() toolCatalogMessage: string = '';
  @Input() suggestionsPlacement: ComposerSuggestionsPlacement = 'hidden';
  @Input() minimal: boolean = false;
  @Input() showSendAction: boolean = true;
  @Input() showMicrophoneAction: boolean = true;
  @Input() customPlaceholder: string = '';
  @Input() currentThread: any = null;
  @Input() currentThreadId: string | null = null;
  @Input() threadMessages: any[] = [];
  @Input() viewOptions: ChatThreadViewOption[] = [];
  @Input() currentDisplayMode: ChatDisplayMode = 'standard';
  @Input() renderPreviewsInline = false;
  @Input() isLocalDevelopment: boolean = false;
  @Input() useLocalBackend: boolean = false;
  @Input() hostedBackendLabel: string = 'Production';
  @Input() forceCompactionLoading: boolean = false;
  @Input() threadAssetItems: ChatThreadAssetItem[] = [];
  @Input() contextUid: string = '';
  @Input() agencyId: string = '';
  @Input() remainingCredits: number | null = null;
  @Input() creditBalanceLoading: boolean = false;
  @Input() lowCreditThreshold: number = 2000;
  @Input() playbookPrefillSlug: string | null = null;
  @Input() playbookPrefillFieldValues: Record<string, string> = {};
  
  @Output() messageChanged = new EventEmitter<InputChangeEvent>();
  @Output() messageSent = new EventEmitter<void>();
  @Output() expandToggled = new EventEmitter<boolean>();
  @Output() microphoneToggled = new EventEmitter<void>();
  @Output() inputKeyDown = new EventEmitter<ChatKeyboardEvent>();
  @Output() textareaKeyDown = new EventEmitter<ChatKeyboardEvent>();
  @Output() composerActionSelected = new EventEmitter<'prompts' | 'agent' | 'context' | 'settings' | 'model' | 'details'>();
  @Output() removeAttachedContext = new EventEmitter<AttachedContextItem>();
  @Output() removeAttachedImage = new EventEmitter<ChatImageAttachment>();
  @Output() removeAttachedFile = new EventEmitter<ChatFileAttachment>();
  @Output() imageFilesSelected = new EventEmitter<File[]>();
  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() imageFilesPasted = new EventEmitter<File[]>();
  @Output() transcriptionCancelled = new EventEmitter<void>();
  @Output() addMenuRequested = new EventEmitter<void>();
  @Output() agentChipClicked = new EventEmitter<void>();
  @Output() modeChipClicked = new EventEmitter<void>();
  @Output() modelChipClicked = new EventEmitter<void>();
  @Output() toolSelectorOpened = new EventEmitter<void>();
  @Output() agentChanged = new EventEmitter<string>();
  @Output() modeChanged = new EventEmitter<'ask' | 'agent'>();
  @Output() modelChanged = new EventEmitter<ModelOption>();
  @Output() toolSelectionChanged = new EventEmitter<string[]>();
  @Output() audioModelChanged = new EventEmitter<string>();
  @Output() imageModelChanged = new EventEmitter<string>();
  @Output() videoModelChanged = new EventEmitter<string>();
  @Output() audioVoiceChanged = new EventEmitter<string>();
  @Output() playbookActivated = new EventEmitter<PlaybookEntry>();
  @Output() playbookSubmitted = new EventEmitter<PlaybookSubmission>();
  @Output() contextItemSelected = new EventEmitter<any>();
  @Output() businessSelectedForContext = new EventEmitter<{ uid: string; name: string }>();
  @Output() businessAttachedForContext = new EventEmitter<{ uid: string; name: string }>();
  @Output() viewOptionSelected = new EventEmitter<ChatThreadViewAction>();
  @Output() displayModePreferenceChanged = new EventEmitter<ChatDisplayMode>();
  @Output() renderPreviewsInlinePreferenceChanged = new EventEmitter<boolean>();
  @Output() backendPreferenceChanged = new EventEmitter<boolean>();
  @Output() threadAssetSelected = new EventEmitter<ChatThreadAssetItem>();
  @Output() renameThreadRequested = new EventEmitter<string>();
  @Output() newConversationRequested = new EventEmitter<void>();
  @Output() deleteThreadRequested = new EventEmitter<void>();
  @Output() forceCompactionRequested = new EventEmitter<void>();
  @Output() selectorModalClosed = new EventEmitter<void>();
  @Output() addCreditsRequested = new EventEmitter<void>();
  @Output() creditsPageRequested = new EventEmitter<void>();

  @ViewChild('chatTextarea') chatTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('composerEl') composerEl!: ElementRef<HTMLElement>;
  @ViewChild('toolbarLeft') toolbarLeft?: ElementRef<HTMLElement>;
  @ViewChild('toolbarLeading') toolbarLeading?: ElementRef<HTMLElement>;
  @ViewChild('imageAttachmentInput') imageAttachmentInput?: ElementRef<HTMLInputElement>;
  @ViewChild('fileAttachmentInput') fileAttachmentInput?: ElementRef<HTMLInputElement>;
  @ViewChild('attachmentPopoverTemplate') attachmentPopoverTemplate?: TemplateRef<any>;
  @ViewChild('addMenuPopoverTemplate') addMenuPopoverTemplate?: TemplateRef<any>;
  @ViewChild('moreMenuPopoverTemplate') moreMenuPopoverTemplate?: TemplateRef<any>;
  @ViewChild('connectorsModalTemplate') connectorsModalTemplate?: TemplateRef<any>;
  @ViewChild('devicesModalTemplate') devicesModalTemplate?: TemplateRef<any>;
  @ViewChild('skillsModalTemplate') skillsModalTemplate?: TemplateRef<any>;
  @ViewChild('agentPopoverTemplate') agentPopoverTemplate?: TemplateRef<any>;
  @ViewChild('modePopoverTemplate') modePopoverTemplate?: TemplateRef<any>;
  @ViewChild('selectorModalHeaderTemplate') selectorModalHeaderTemplate?: TemplateRef<any>;
  @ViewChild('modelHeaderTemplate') modelHeaderTemplate?: TemplateRef<any>;
  @ViewChild('modelHeaderActionsTemplate') modelHeaderActionsTemplate?: TemplateRef<any>;
  @ViewChild('modelPopoverTemplate') modelPopoverTemplate?: TemplateRef<any>;
  @ViewChild('toolPopoverTemplate') toolPopoverTemplate?: TemplateRef<any>;
  @ViewChild('promptPopoverTemplate') promptPopoverTemplate?: TemplateRef<any>;
  @ViewChild('contextPopoverTemplate') contextPopoverTemplate?: TemplateRef<any>;
  @ViewChild('contextFooterTemplate') contextFooterTemplate?: TemplateRef<any>;
  @ViewChild('contextPopoverRef') contextPopoverRef?: any;
  @ViewChild('viewPopoverTemplate') viewPopoverTemplate?: TemplateRef<any>;
  @ViewChild('contextUsagePopoverTemplate') contextUsagePopoverTemplate?: TemplateRef<any>;
  @ViewChild('threadDetailsPopoverTemplate') threadDetailsPopoverTemplate?: TemplateRef<any>;
  @ViewChild('threadAssetsHeaderTemplate') threadAssetsHeaderTemplate?: TemplateRef<any>;
  @ViewChild('threadAssetsTemplate') threadAssetsTemplate?: TemplateRef<any>;
  @ViewChild('threadAssetSearchInput') threadAssetSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChildren('threadAssetItemButton') threadAssetItemButtons?: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChild('fieldSelectTemplate') fieldSelectTemplate?: TemplateRef<any>;
  @ViewChild('contextFieldTemplate') contextFieldTemplate?: TemplateRef<any>;
  @ViewChild('systemPromptTemplate') systemPromptTemplate?: TemplateRef<any>;

  // Context playbooks for suggestions
  contextPrompts: PlaybookEntry[] = [];
  
  // Hover popover state
  hoveredPrompt: PlaybookEntry | null = null;
  hoverTimeout: any = null;
  popoverPosition = { x: 0, y: 0, width: 0 };
  private readonly maxComposerHeight = 220;
  
  // Popover data
  availableAgents: AgentOption[] = agentSelectorOptions;
  modeOptions: { value: 'ask' | 'agent'; label: string }[] = [
    { value: 'ask', label: 'Ask' },
    { value: 'agent', label: 'Agent' }
  ];

  // Playbook state
  availablePlaybooks: PlaybookEntry[] = [];
  activePlaybook: PlaybookEntry | null = null;
  playbookFieldValues: Record<string, string> = {};
  showPromptPopover = false;
  selectedPromptIndex = 0;
  showSystemPrompt = false;
  editableSystemPrompt = '';
  systemPromptOverrides: Record<string, string> = {};
  fieldSelectField: PlaybookField | null = null;
  fieldSelectCustomValue = '';
  deviceCommandModalTarget: ChatDeviceCommandTarget | null = null;

  // Context field state (for playbook context-type fields)
  contextFieldActive: PlaybookField | null = null;
  contextFieldItems: any[] = [];
  contextFieldBusinessUid: string = '';
  contextFieldNewName: string = '';
  contextFieldSegment: 'existing' | 'new' = 'existing';
  playbookContextEntities: Record<string, { id: string; name: string; type: string }> = {};
  private contextFieldItemSubs: Subscription[] = [];

  // Context popover state
  contextItems: any[] = [];
  showContextPopover = false;
  selectedContextIndex = 0;
  contextUserObject: any = null;
  agencyObject: any = null;
  contextBusinessName: string = '';
  contextPickerInitialSource: ContextAttachmentSource = 'business';
  availableSkills: AgencySkillSummary[] = [];
  selectedSkillIndex = 0;

  showThreadDetailsPopover = false;
  activeSelectorModal: SelectorModalKind | null = null;
  activeModelSettingsTab: ModelSettingsTab = 'default';
  selectedThreadAssetFilter: ThreadAssetFilter = 'all';
  threadAssetSearchQuery = '';
  focusedThreadAssetIndex = -1;
  private optimisticToolCatalogLoading = false;
  private skillsSubscription?: Subscription;
  private slashSkillModalQueued = false;

  private contextItemSubscriptions: Subscription[] = [];
  private optimisticModelCatalogLoading = false;
  private activeModelInfoTooltipElement: HTMLElement | null = null;
  private activeModelInfoTooltipText: string | null = null;
  private activePlaybookSource: 'internal' | 'external' | null = null;
  private lastExternalPlaybookSignature: string | null = null;
  private pendingThreadAssetSelection: ChatThreadAssetItem | null = null;
  private composerAttachedImageItems: ChatImageAttachment[] = [];
  private composerAttachedFileItems: ChatFileAttachment[] = [];
  private attachmentLayoutObserver?: ResizeObserver;
  private attachmentLayoutFrameId: number | null = null;
  private collapseComposerAttachments = false;

  constructor(
    private afs: AngularFirestore,
    private afStorage: AngularFireStorage,
    private uiModalService: UiModalService,
    private popoverService: UiPopoverService,
    private tooltipService: UiTooltipService,
    private attachedContextService: AttachedContextService,
    private actionsPaletteService: ActionsPaletteService,
    private authService: AuthService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    public theme: ThemeService
  ) {}

  // Combined loading state for both regular loading and thread initialization
  get isLoadingOrInitializing(): boolean {
    return this.loading || this.initializingThread;
  }

  get activeThreadId(): string | null {
    return this.currentThread?.id || this.currentThreadId || null;
  }

  get hasSkillChipsInMessage(): boolean {
    return this.composerSkillParts.some(part => part.type === 'skill');
  }

  get composerSkillParts(): ComposerSkillTokenPart[] {
    return this.buildComposerSkillParts(this.newMessage || '');
  }

  get shouldShowStopAction(): boolean {
    if (this.initializingThread || !this.activeThreadId) {
      return false;
    }

    return this.loading || this.currentThread?.status === 'running' || this.currentThread?.status === 'continuing';
  }

  // Dynamic placeholder text based on state
  get placeholderText(): string {
    if (this.initializingThread) {
      return 'Initializing thread...';
    }
    if (this.loading) {
      return 'Processing...';
    }
    return this.customPlaceholder || '...';
  }

  get agentLabel(): string {
    return this.getAgentLabel();
  }

  get selectedAgentOption(): AgentOption | undefined {
    return this.availableAgents.find((agentOption) => agentOption.value === this.agent);
  }

  get shouldShowCreditChip(): boolean {
    return this.isCreditLow;
  }

  get isCreditDepleted(): boolean {
    return !this.creditBalanceLoading
      && typeof this.remainingCredits === 'number'
      && this.remainingCredits <= 0;
  }

  get isCreditLow(): boolean {
    return !this.creditBalanceLoading
      && typeof this.remainingCredits === 'number'
      && this.remainingCredits <= this.lowCreditThreshold;
  }

  get creditChipLabel(): string {
    if (this.creditBalanceLoading) {
      return 'Credits';
    }

    return `${this.formatCreditCount(this.remainingCredits || 0)} credits`;
  }

  get creditChipTitle(): string {
    if (this.creditBalanceLoading) {
      return 'Loading agency credits';
    }

    if (this.isCreditDepleted) {
      return 'No agency credits remaining. Add credits to continue.';
    }

    return `${this.formatCreditCount(this.remainingCredits || 0)} agency credits remaining`;
  }

  get isNonDefaultAgent(): boolean {
    return this.agent !== defaultAgentSlug;
  }

  get modeLabel(): string {
    return this.currentMode === 'ask' ? 'Ask' : 'Agent';
  }

  get modelLabel(): string {
    const label = this.currentModelLabel || '';
    if (!label || !this.currentModelAvatar) return label;
    const colonIndex = label.indexOf(': ');
    return colonIndex !== -1 ? label.slice(colonIndex + 2) : label;
  }

  get currentModelBadgeStyle(): Record<string, string> {
    const providerKey = this.currentModelKey ? this.currentModelKey.split('/')[0] : '';
    return providerKey ? this.getProviderBadgeStyleForProvider(providerKey) : {};
  }

  get currentModelAvatar(): string {
    const providerKey = this.currentModelKey ? this.currentModelKey.split('/')[0] : '';
    return providerKey ? this.getProviderAvatarForProvider(providerKey) : '';
  }

  get hasActiveSelectorModal(): boolean {
    return this.activeSelectorModal !== null;
  }

  get activeSelectorModalHeaderTitle(): string {
    return this.activeSelectorModal ? (SELECTOR_MODAL_HEADER_CONTENT[this.activeSelectorModal]?.title || '') : '';
  }

  get activeSelectorModalHeaderSubtitle(): string {
    return this.activeSelectorModal ? (SELECTOR_MODAL_HEADER_CONTENT[this.activeSelectorModal]?.subtitle || '') : '';
  }

  get activeSelectorModalHeaderShortcut(): SelectorModalShortcutHint | undefined {
    return this.activeSelectorModal ? SELECTOR_MODAL_HEADER_CONTENT[this.activeSelectorModal]?.shortcutHint : undefined;
  }

  get modelShortcutHint(): SelectorModalShortcutHint | undefined {
    return SELECTOR_MODAL_HEADER_CONTENT.model?.shortcutHint;
  }

  get hasViewOptions(): boolean {
    return this.viewOptions.length > 0;
  }

  get showViewButton(): boolean {
    return this.hasViewOptions;
  }

  get canOpenViewModal(): boolean {
    return this.hasViewOptions && !!this.activeThreadId;
  }

  get showThreadAssetsButton(): boolean {
    return !!this.activeThreadId && this.threadAssetItems.length > 0;
  }

  get threadAssetsButtonTitle(): string {
    const assetCount = this.threadAssetItems.length;
    const assetLabel = assetCount === 1 ? 'asset' : 'assets';
    return `Browse ${assetCount} thread ${assetLabel}`;
  }

  get selectedToolCount(): number {
    return this.selectedToolNames.length;
  }

  get toolSelectionBadgeText(): string {
    return this.selectedToolCount > 0 ? String(this.selectedToolCount) : 'All';
  }

  get addMenuOptions(): AddMenuOption[] {
    return ADD_MENU_OPTIONS;
  }

  trackAddMenuOption(index: number, option: AddMenuOption): AddMenuAction {
    return option.value;
  }

  get moreMenuOptions(): MoreMenuOption[] {
    const options: MoreMenuOption[] = [];
    const hasUserDeviceAccess = !!this.authService.authStateUser?.uid;
    const hasUserMcpAccess = !!this.authService.authStateUser?.uid;

    if (this.availablePlaybooks.length > 0) {
      options.push({
        value: 'playbooks',
        label: 'Playbooks',
        description: 'Open the structured playbooks available for this agent.',
        iconClass: 'fa-regular fa-bolt'
      });
    }

    options.push({
      value: 'mode',
      label: 'Mode',
      description: 'Switch this thread between agent mode and ask mode.',
      iconClass: 'fa-regular fa-sliders',
      badgeText: this.modeLabel
    });

    options.push({
      value: 'credits',
      label: 'Credits',
      description: 'View agency credit balance and usage.',
      iconClass: 'fa-regular fa-coins',
      badgeText: this.creditMenuBadgeText
    });

    options.push(
      {
        value: 'history',
        label: 'History',
        description: 'Search and reopen the agency thread history.',
        iconClass: 'fa-regular fa-magnifying-glass'
      },
      {
        value: 'connectors',
        label: 'Connectors',
        description: 'Configure MCPs that your agent can access.',
        iconClass: 'fa-regular fa-circle-nodes',
        disabled: !hasUserMcpAccess
      },
      {
        value: 'devices',
        label: 'Devices',
        description: 'Inspect registered computers, browsers, and their recent command activity.',
        iconClass: 'fa-regular fa-laptop-mobile',
        disabled: !hasUserDeviceAccess
      },
      {
        value: 'skills',
        label: 'Skills',
        description: 'Insert agency skill triggers into this thread.',
        iconClass: 'fa-regular fa-wand-magic-sparkles',
        badgeText: this.availableSkills.length ? String(this.availableSkills.length) : undefined
      },
      {
        value: 'tools',
        label: 'Tools',
        description: 'Choose which tools the agent can call for this thread.',
        iconClass: 'fa-regular fa-screwdriver-wrench',
        badgeText: this.toolSelectionBadgeText
      },
      {
        value: 'view',
        label: 'Change view',
        description: 'Move this thread between page, floating, pinned, fullscreen, or minimized layouts.',
        iconClass: 'fa-regular fa-up-right-from-square',
        disabled: !this.canOpenViewModal
      }
    );

    if (this.showThreadAssetsButton) {
      options.push({
        value: 'assets',
        label: 'Thread assets',
        description: 'Browse previews, generated media, and emitted files for this thread.',
        iconClass: 'fa-regular fa-folder',
        badgeText: String(this.threadAssetItems.length)
      });
    }

    if (this.activeThreadId) {
      options.push({
        value: 'details',
        label: 'Thread details',
        description: 'Open credits, compute, rename, and delete controls for this conversation.',
        iconClass: 'fa-regular fa-circle-question'
      });
    }

    return options;
  }

  trackMoreMenuOption(index: number, option: MoreMenuOption): MoreMenuAction {
    return option.value;
  }

  get creditMenuBadgeText(): string | undefined {
    if (this.creditBalanceLoading) {
      return 'Loading';
    }

    if (typeof this.remainingCredits !== 'number') {
      return undefined;
    }

    return `${this.formatCreditCount(this.remainingCredits)} left`;
  }

  get threadAssetFilterOptions(): ThreadAssetFilterOption[] {
    return THREAD_ASSET_FILTER_DEFINITIONS.filter(option => {
      if (option.value === 'all') {
        return true;
      }

      return this.threadAssetItems.some(asset => option.kinds.includes(asset.kind));
    }).map(({ value, label }) => ({ value, label }));
  }

  get filteredThreadAssetItems(): ChatThreadAssetItem[] {
    const baseItems = this.selectedThreadAssetFilter === 'all'
      ? this.threadAssetItems
      : this.threadAssetItems.filter(asset => this.getThreadAssetFilterKinds(this.selectedThreadAssetFilter).includes(asset.kind));

    const searchQuery = this.threadAssetSearchQuery.trim();
    if (!searchQuery) {
      return baseItems;
    }

    return baseItems
      .map((asset, index) => ({
        asset,
        index,
        score: this.calculateThreadAssetSearchScore(asset, searchQuery)
      }))
      .filter(result => result.score > 8)
      .sort((left, right) => right.score - left.score || left.index - right.index)
      .map(result => result.asset);
  }

  get filteredThreadAssetsEmptyLabel(): string {
    const searchQuery = this.threadAssetSearchQuery.trim();
    if (searchQuery) {
      return `No thread assets match “${searchQuery}”.`;
    }

    switch (this.selectedThreadAssetFilter) {
      case 'preview':
        return 'No previews in this thread yet.';
      case 'image':
        return 'No images in this thread yet.';
      case 'audio':
        return 'No audio assets in this thread yet.';
      case 'file':
        return 'No files in this thread yet.';
      default:
        return 'No thread assets yet.';
    }
  }

  get filteredThreadAssetsEmptyIconClass(): string {
    if (this.threadAssetSearchQuery.trim()) {
      return 'fa-regular fa-magnifying-glass';
    }

    switch (this.selectedThreadAssetFilter) {
      case 'preview':
        return 'fa-regular fa-window-maximize';
      case 'image':
        return 'fa-regular fa-image';
      case 'audio':
        return 'fa-regular fa-file-audio';
      case 'file':
        return 'fa-regular fa-file-lines';
      default:
        return 'fa-regular fa-folder-open';
    }
  }

  get showInlineLocalBackendIndicator(): boolean {
    return this.isLocalDevelopment && this.useLocalBackend && !this.newMessage.trim();
  }

  get displayModeOptions(): DisplayModeOption[] {
    return DISPLAY_MODE_OPTIONS;
  }

  get inlinePreviewModeOptions(): InlinePreviewModeOption[] {
    return INLINE_PREVIEW_MODE_OPTIONS;
  }

  get localBackendIndicatorTitle(): string {
    return 'Local backend active. This thread will route to the local agent backend.';
  }

  get contextUsage(): any | null {
    return this.currentThread?.contextUsage || null;
  }

  get hasContextUsage(): boolean {
    return !!this.contextUsage && typeof this.contextUsage.promptTokens === 'number';
  }

  get shouldShowContextUsageButton(): boolean {
    return this.hasThread;
  }

  get contextFillRatio(): number | null {
    const usage = this.contextUsage;
    if (!usage) {
      return null;
    }

    if (typeof usage.fillRatio === 'number' && Number.isFinite(usage.fillRatio)) {
      return Math.max(0, Math.min(1, usage.fillRatio));
    }

    if (typeof usage.promptTokens === 'number' && typeof usage.contextWindowTokens === 'number' && usage.contextWindowTokens > 0) {
      return Math.max(0, Math.min(1, usage.promptTokens / usage.contextWindowTokens));
    }

    return null;
  }

  get contextFillPercent(): number | null {
    const ratio = this.contextFillRatio;
    return ratio === null ? null : Math.round(ratio * 100);
  }

  get contextUsageButtonLabel(): string {
    const percent = this.contextFillPercent;
    if (percent !== null) {
      return `${percent}%`;
    }

    if (this.hasContextUsage) {
      return this.formatContextTokenCount(this.contextUsage.promptTokens);
    }

    return 'Ctx';
  }

  get contextUsageButtonTitle(): string {
    if (!this.hasContextUsage) {
      return 'Context window usage is not available yet.';
    }

    const usage = this.contextUsage;
    const promptTokens = this.formatContextTokenCount(usage.promptTokens);
    const windowTokens = typeof usage.contextWindowTokens === 'number'
      ? this.formatContextTokenCount(usage.contextWindowTokens)
      : 'unknown window';
    const percent = this.contextFillPercent !== null ? `${this.contextFillPercent}% full` : 'usage recorded';
    return `${percent}: ${promptTokens} / ${windowTokens}`;
  }

  get contextUsageRingStyle(): Record<string, string> {
    const ratio = this.contextFillRatio ?? 0;
    return {
      '--context-fill': `${Math.round(ratio * 360)}deg`
    };
  }

  get contextUsageLevelClass(): string {
    const ratio = this.contextFillRatio ?? 0;
    if (ratio >= 0.9) {
      return 'is-full';
    }
    if (ratio >= 0.7) {
      return 'is-near';
    }
    return 'is-normal';
  }

  get contextUsageSourceLabel(): string {
    const source = this.contextUsage?.source;
    if (source === 'provider_usage') {
      return 'Provider usage';
    }
    if (source === 'estimate') {
      return 'Estimated';
    }
    return 'Unknown';
  }

  get canForceCompaction(): boolean {
    const status = this.currentThread?.status;
    return !!this.activeThreadId
      && !this.forceCompactionLoading
      && status !== 'running'
      && status !== 'continuing';
  }

  get forceCompactionDisabledReason(): string {
    if (!this.activeThreadId) {
      return 'Open a thread before forcing compaction.';
    }

    if (this.forceCompactionLoading) {
      return 'Compaction is already running.';
    }

    const status = this.currentThread?.status;
    if (status === 'running' || status === 'continuing') {
      return 'Stop the current run before forcing compaction.';
    }

    return '';
  }

  get contextUsageUpdatedAtLabel(): string {
    const updatedAt = this.contextUsage?.updatedAt;
    const date = updatedAt?.toDate ? updatedAt.toDate() : updatedAt instanceof Date ? updatedAt : null;
    if (!date) {
      return 'Unknown';
    }

    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  get contextUsageWindowLabel(): string {
    const usage = this.contextUsage;
    if (!usage || typeof usage.contextWindowTokens !== 'number') {
      return 'Unknown';
    }

    return this.formatContextTokenCount(usage.contextWindowTokens);
  }

  get contextUsagePromptLabel(): string {
    const usage = this.contextUsage;
    if (!usage || typeof usage.promptTokens !== 'number') {
      return 'Unknown';
    }

    return this.formatContextTokenCount(usage.promptTokens);
  }

  get displayModelCatalogStatus(): 'loading' | 'live' | 'error' {
    return this.optimisticModelCatalogLoading ? 'loading' : this.modelCatalogStatus;
  }

  get displayModelCatalogSourceLabel(): string {
    return this.modelCatalogSourceLabel || 'Model catalog';
  }

  get displayModelCatalogMessage(): string {
    if (this.optimisticModelCatalogLoading && this.modelCatalogStatus !== 'loading') {
      return 'Opening the picker and requesting the live catalog from the selected backend.';
    }

    return this.modelCatalogMessage || 'Requesting the live model catalog from the selected backend.';
  }

  get displayToolCatalogStatus(): 'loading' | 'live' | 'error' {
    return this.optimisticToolCatalogLoading ? 'loading' : this.toolCatalogStatus;
  }

  get displayToolCatalogSourceLabel(): string {
    return this.toolCatalogSourceLabel || 'Tool catalog';
  }

  get displayToolCatalogMessage(): string {
    if (this.optimisticToolCatalogLoading && this.toolCatalogStatus !== 'loading') {
      return 'Opening the picker and requesting the live tool catalog from the selected backend.';
    }

    return this.toolCatalogMessage || 'Requesting the live tool catalog from the selected backend.';
  }

  get displayAttachedImageItems(): ChatImageAttachment[] {
    return this.composerAttachedImageItems;
  }

  get displayAttachedFileItems(): ChatFileAttachment[] {
    return this.composerAttachedFileItems;
  }

  get allComposerAttachmentItems(): ComposerAttachmentListItem[] {
    const contextItems = this.attachedItems.map((item): ComposerAttachmentListItem => ({
      key: `context:${item.id}`,
      type: 'context',
      label: this.getAttachedDisplayName(item),
      iconClass: this.getAttachedContextIconClass(item),
      status: 'ready',
      item
    }));

    const imageItems = this.displayAttachedImageItems.map((item): ComposerAttachmentListItem => ({
      key: `image:${item.id}`,
      type: 'image',
      label: item.name || 'Image',
      iconClass: 'fa-regular fa-image',
      status: item.status,
      item
    }));

    const fileItems = this.displayAttachedFileItems.map((item): ComposerAttachmentListItem => ({
      key: `file:${item.id}`,
      type: 'file',
      label: item.name || 'File',
      iconClass: 'fa-regular fa-file-lines',
      status: item.status,
      item
    }));

    return [...contextItems, ...imageItems, ...fileItems];
  }

  get totalComposerAttachmentCount(): number {
    return this.allComposerAttachmentItems.length;
  }

  get shouldGroupComposerAttachments(): boolean {
    return this.collapseComposerAttachments;
  }

  get inlineComposerAttachmentItems(): ComposerAttachmentListItem[] {
    return this.shouldGroupComposerAttachments ? [] : this.allComposerAttachmentItems;
  }

  getModelCatalogBodyMessage(): string {
    if (this.displayModelCatalogStatus === 'loading') {
      return this.displayModelCatalogMessage;
    }

    if (this.displayModelCatalogStatus === 'error') {
      return this.displayModelCatalogMessage || 'Could not load the live model catalog from the selected backend.';
    }

    if (this.groupedAvailableModels.length === 0) {
      return 'No live models loaded from the selected backend yet.';
    }

    return `${this.availableModels.length} live models loaded from ${this.displayModelCatalogSourceLabel}.`;
  }

  getModelCatalogHeaderSubtitle(): string {
    if (this.displayModelCatalogStatus === 'loading') {
      return `Loading live catalog from ${this.displayModelCatalogSourceLabel}.`;
    }

    if (this.displayModelCatalogStatus === 'error') {
      return `Could not load ${this.displayModelCatalogSourceLabel}.`;
    }

    const modelLabel = this.availableModels.length === 1 ? 'model' : 'models';
    return `${this.availableModels.length} ${modelLabel} loaded from ${this.displayModelCatalogSourceLabel}.`;
  }

  getToolCatalogBodyMessage(): string {
    if (this.displayToolCatalogStatus === 'loading') {
      return this.displayToolCatalogMessage;
    }

    if (this.displayToolCatalogStatus === 'error') {
      return this.displayToolCatalogMessage || 'Could not load the live tool catalog from the selected backend.';
    }

    if (this.groupedAvailableTools.length === 0) {
      return 'No live tools loaded from the selected backend yet.';
    }

    const selectedSummary = this.selectedToolCount > 0
      ? `${this.selectedToolCount} selected`
      : 'All tools available';
    const toolLabel = this.availableTools.length === 1 ? 'tool' : 'tools';
    return `${this.availableTools.length} ${toolLabel} loaded from ${this.displayToolCatalogSourceLabel}. ${selectedSummary}.`;
  }

  get isRecommendedSortEnabled(): boolean {
    return this.recommendedGroupingMode === 'all';
  }

  get activePriceBlendPreset(): PriceBlendPreset {
    return this.priceBlendPresets.find(preset => preset.key === this.priceBlendPresetKey) || this.priceBlendPresets[1]!;
  }

  setRecommendedGroupingMode(mode: RecommendedGroupingMode): void {
    if (this.recommendedGroupingMode === mode) {
      return;
    }

    this.recommendedGroupingMode = mode;
    this.isPriceBlendSelectorOpen = false;
    this.refreshSortedRecommendedEntries();
  }

  toggleRecommendedSortDirection(): void {
    this.recommendedSortDirection = this.recommendedSortDirection === 'asc' ? 'desc' : 'asc';
    this.refreshSortedRecommendedEntries();
  }

  getRecommendedSortLabel(): string {
    return this.recommendedSortDirection === 'asc'
      ? `Sort order: low to high blended cost (${this.activePriceBlendPreset.ratioLabel})`
      : `Sort order: high to low blended cost (${this.activePriceBlendPreset.ratioLabel})`;
  }

  getRecommendedSortHelperText(): string {
    if (this.isRecommendedSortEnabled) {
      return `Order the recommended list by ${this.activePriceBlendPreset.ratioLabel} blended cost while still showing raw input and output pricing.`;
    }

    return 'By provider keeps the curated provider lanes intact. Switch to All to rank every recommended model together.';
  }

  togglePriceBlendSelector(event: MouseEvent): void {
    event.stopPropagation();
    this.isPriceBlendSelectorOpen = !this.isPriceBlendSelectorOpen;
  }

  setPriceBlendPreset(presetKey: PriceBlendPresetKey, event?: Event): void {
    event?.stopPropagation();

    if (this.priceBlendPresetKey === presetKey && this.isPriceBlendSelectorOpen) {
      this.isPriceBlendSelectorOpen = false;
      return;
    }

    this.priceBlendPresetKey = presetKey;
    this.isPriceBlendSelectorOpen = false;
    this.refreshGroupedAvailableModels();
  }

  get audioCapableModels(): ModelOption[] {
    return this.availableModels
      .filter(model => (model.outputModalities ?? []).includes('audio'))
      .sort((left, right) => left.displayName.localeCompare(right.displayName));
  }

  get imageCapableModels(): ModelOption[] {
    return this.availableModels
      .filter(model => (model.outputModalities ?? []).includes('image'))
      .sort((left, right) => left.displayName.localeCompare(right.displayName));
  }

  get videoCapableModels(): ModelOption[] {
    return [...this.availableVideoModels]
      .sort((left, right) => left.displayName.localeCompare(right.displayName));
  }

  get effectiveAudioModelKey(): string {
    return this.currentAudioModelKey || this.defaultAudioModelKey || '';
  }

  get effectiveImageModelKey(): string {
    return this.currentImageModelKey || this.defaultImageModelKey || '';
  }

  get effectiveVideoModelKey(): string {
    return this.currentVideoModelKey || this.defaultVideoModelKey || '';
  }

  get currentAudioVoiceOptions(): Array<{ value: string; label: string }> {
    const systemDefaultVoice = this.formatAudioVoiceLabel(this.defaultAudioVoice || 'alloy');
    const fallbackOption = [{ value: '', label: `System default (${systemDefaultVoice})` }];
    const modelKey = this.effectiveAudioModelKey;
    const configuredOptions = modelKey ? (this.audioVoiceOptionsByModel[modelKey] || []) : [];

    if (!configuredOptions.length) {
      return fallbackOption;
    }

    return configuredOptions.map(option => option.value === ''
      ? { ...option, label: `System default (${systemDefaultVoice})` }
      : option);
  }

  get activeModelBrowserSubtitle(): string {
    switch (this.activeModelSettingsTab) {
      case 'audio':
        return 'Pick the default native audio model and its voice for this thread.';
      case 'image':
        return 'Pick the default native image model for this thread.';
      case 'video':
        return 'Pick the default native video model for this thread.';
      default:
        return 'Choose the thread’s default chat model or browse the curated provider lanes.';
    }
  }

  get activeMediaCatalogModels(): ModelOption[] {
    switch (this.activeModelSettingsTab) {
      case 'audio':
        return this.audioCapableModels;
      case 'image':
        return this.imageCapableModels;
      case 'video':
        return this.videoCapableModels;
      default:
        return [];
    }
  }

  get selectedMediaModelKeyForActiveTab(): string {
    switch (this.activeModelSettingsTab) {
      case 'audio':
        return this.currentAudioModelKey;
      case 'image':
        return this.currentImageModelKey;
      case 'video':
        return this.currentVideoModelKey;
      default:
        return this.currentModelKey;
    }
  }

  get defaultAudioModelLabel(): string {
    return this.getModelDisplayName(this.defaultAudioModelKey, this.availableModels, 'Backend default audio model');
  }

  get defaultImageModelLabel(): string {
    return this.getModelDisplayName(this.defaultImageModelKey, this.availableModels, 'Backend default image model');
  }

  get defaultVideoModelLabel(): string {
    return this.getModelDisplayName(this.defaultVideoModelKey, this.availableVideoModels, 'Backend default video model');
  }

  get audioSystemDefaultDescription(): string {
    const summaryParts = [this.defaultAudioModelLabel];

    if (this.currentAudioVoice) {
      summaryParts.push(`Voice override ${this.formatAudioVoiceLabel(this.currentAudioVoice)}`);
    } else if (this.defaultAudioVoice) {
      summaryParts.push(`Default voice ${this.formatAudioVoiceLabel(this.defaultAudioVoice)}`);
    }

    return summaryParts.join(' · ');
  }

  get imageSystemDefaultDescription(): string {
    return this.defaultImageModelLabel;
  }

  get videoSystemDefaultDescription(): string {
    return this.defaultVideoModelLabel;
  }

  get mediaCatalogEmptyMessage(): string {
    switch (this.activeModelSettingsTab) {
      case 'audio':
        return 'No audio-capable models are available in the live catalog right now.';
      case 'image':
        return 'No image-capable models are available in the live catalog right now.';
      case 'video':
        return 'No video-capable models are available in the live catalog right now.';
      default:
        return this.getModelCatalogBodyMessage();
    }
  }

  setActiveModelSettingsTab(tab: ModelSettingsTab): void {
    if (this.activeModelSettingsTab === tab) {
      return;
    }

    this.hideModelInfoTooltip(true);
    this.activeModelSettingsTab = tab;
  }

  private buildGroupedAvailableModels(): ModelGroup[] {
    if (this.providerRecommendations.length > 0) {
      return this.buildGroupedAvailableModelsFromRecommendations();
    }

    return this.buildGroupedAvailableModelsFromFallback();
  }

  private buildGroupedAvailableModelsFromFallback(): ModelGroup[] {
    const groups = this.providerDefinitions
      .map(provider => this.buildProviderGroup(provider))
      .filter((group): group is ModelGroup => !!group);

    const currentModel = this.availableModels.find(model => model.key === this.currentModelKey);
    if (currentModel) {
      const currentProvider = currentModel.provider || this.currentModelKey.split('/')[0];
      const represented = groups.some(group => group.key === currentProvider);

      if (!represented) {
        groups.unshift({
          key: 'current',
          label: 'Current Thread Model',
          description: 'This thread is pinned to a provider outside the main shortlist.',
          count: 1,
          recommendedEntries: [this.enrichEntry({
            model: currentModel,
            tier: 'Current',
            summary: 'Existing selection'
          })],
          missingTiers: [],
          additionalEntries: [],
          badgeStyle: this.getProviderBadgeStyleForProvider('current'),
          avatar: this.getProviderAvatarForProvider('current')
        });
      }
    }

    return groups;
  }

  private buildGroupedAvailableModelsFromRecommendations(): ModelGroup[] {
    const recommendationKeys = this.providerRecommendations.map(recommendation => recommendation.provider);
    const providerKeys = Array.from(new Set([
      ...recommendationKeys,
      ...this.availableModels
        .filter(model => this.isLogicalChatModel(model))
        .map(model => model.provider || model.key.split('/')[0] || 'openrouter')
    ]));
    const recommendationMap = new Map(this.providerRecommendations.map(recommendation => [recommendation.provider, recommendation]));

    const groups = providerKeys
      .map(providerKey => {
        const recommendation = recommendationMap.get(providerKey);
        if (recommendation) {
          return this.buildProviderGroupFromRecommendation(recommendation);
        }

        return this.buildUncuratedProviderGroup(providerKey);
      })
      .filter((group): group is ModelGroup => !!group);

    const currentModel = this.availableModels.find(model => model.key === this.currentModelKey);
    if (currentModel) {
      const currentProvider = currentModel.provider || this.currentModelKey.split('/')[0];
      const represented = groups.some(group => group.key === currentProvider);

      if (!represented) {
        groups.unshift({
          key: 'current',
          label: 'Current Thread Model',
          description: 'This thread is pinned to a provider outside the recommended shortlist.',
          count: 1,
          recommendedEntries: [this.enrichEntry({
            model: currentModel,
            tier: 'Current',
            summary: 'Existing selection'
          })],
          missingTiers: [],
          additionalEntries: [],
          badgeStyle: this.getProviderBadgeStyleForProvider('current'),
          avatar: this.getProviderAvatarForProvider('current')
        });
      }
    }

    return groups;
  }

  private buildProviderGroupFromRecommendation(recommendation: OpenRouterProviderRecommendation): ModelGroup | undefined {
    const localProviderDefinition = this.providerDefinitions.find(provider => provider.key === recommendation.provider);
    if (localProviderDefinition) {
      return this.buildProviderGroup(
        localProviderDefinition,
        recommendation.providerLabel,
        recommendation.description
      );
    }

    const providerModels = this.getLogicalProviderModels(recommendation.provider);

    if (providerModels.length === 0) {
      return undefined;
    }

    const recommendedEntries = recommendation.tiers
      .filter(tier => tier.status === 'selected' && !!tier.modelId)
      .map(tier => {
        const model = this.findProviderModel(providerModels, tier.modelId);
        if (!model) {
          return undefined;
        }

        return this.enrichEntry({
          model,
          tierKey: tier.tier,
          tier: tier.label,
          summary: tier.reason
        });
      })
      .filter((entry): entry is ModelGroupEntry => !!entry);
    const recommendedKeys = new Set(recommendedEntries.map(entry => this.getModelIdentityKey(entry.model)));
    const missingTiers = recommendation.tiers
      .filter(tier => tier.status === 'missing')
      .map(tier => ({ tier: tier.label, reason: tier.reason }));
    const additionalEntries = providerModels
      .filter(model => !recommendedKeys.has(this.getModelIdentityKey(model)))
      .sort((left, right) => this.compareProviderModels(left, right))
      .map(model => this.enrichEntry({
        model,
        tier: model.key === this.currentModelKey ? 'Current' : 'More',
        summary: model.key === this.currentModelKey ? 'Current thread model' : 'Available option'
      }));

    return {
      key: recommendation.provider,
      label: recommendation.providerLabel,
      description: recommendation.description,
      count: providerModels.length,
      recommendedEntries,
      missingTiers,
      additionalEntries,
      badgeStyle: this.getProviderBadgeStyleForProvider(recommendation.provider),
      avatar: this.getProviderAvatarForProvider(recommendation.provider)
    };
  }

  private buildUncuratedProviderGroup(providerKey: string): ModelGroup | undefined {
    const providerModels = this.getLogicalProviderModels(providerKey);

    if (providerModels.length === 0) {
      return undefined;
    }

    return {
      key: providerKey,
      label: this.getProviderLabel(providerKey),
      description: 'Additional general-purpose text models available in the live catalog.',
      count: providerModels.length,
      recommendedEntries: [],
      missingTiers: [],
      additionalEntries: providerModels
        .sort((left, right) => this.compareProviderModels(left, right))
        .map(model => this.enrichEntry({
          model,
          tier: model.key === this.currentModelKey ? 'Current' : 'More',
          summary: model.key === this.currentModelKey ? 'Current thread model' : 'Available option'
        })),
      badgeStyle: this.getProviderBadgeStyleForProvider(providerKey),
      avatar: this.getProviderAvatarForProvider(providerKey)
    };
  }

  private findProviderModel(providerModels: ModelOption[], modelKey?: string): ModelOption | undefined {
    if (!modelKey) {
      return undefined;
    }

    const canonicalKey = modelKey.trim();
    return providerModels.find(model =>
      model.key === canonicalKey ||
      model.canonicalSlug === canonicalKey ||
      model.aliasKeys?.includes(canonicalKey)
    );
  }

  private pickFallbackTierModel(providerModels: ModelOption[], tierKey: CuratedTierKey, excludedKeys: Set<string>): ModelOption | undefined {
    const remainingModels = providerModels.filter(model => !excludedKeys.has(this.getModelIdentityKey(model)));

    if (remainingModels.length === 0) {
      return undefined;
    }

    const pricedModels = remainingModels
      .filter(model => this.getComparablePrice(model) !== undefined)
      .sort((left, right) => {
        const leftPrice = this.getComparablePrice(left) ?? Number.POSITIVE_INFINITY;
        const rightPrice = this.getComparablePrice(right) ?? Number.POSITIVE_INFINITY;

        if (leftPrice !== rightPrice) {
          return leftPrice - rightPrice;
        }

        return this.compareProviderModels(left, right);
      });

    const capabilitySortedModels = [...remainingModels].sort((left, right) => this.compareProviderModels(left, right));

    if (tierKey === 'good') {
      return pricedModels[0] || capabilitySortedModels[capabilitySortedModels.length - 1] || capabilitySortedModels[0];
    }

    if (tierKey === 'better') {
      if (pricedModels.length > 0) {
        return pricedModels[Math.floor((pricedModels.length - 1) / 2)];
      }

      return capabilitySortedModels[Math.min(1, capabilitySortedModels.length - 1)] || capabilitySortedModels[0];
    }

    return capabilitySortedModels[0] || pricedModels[pricedModels.length - 1];
  }

  isProviderExpanded(providerKey: string): boolean {
    return !!this.providerExpansionState[providerKey];
  }

  toggleProvider(providerKey: string, event: Event): void {
    event.stopPropagation();
    const headerElement = event.currentTarget as HTMLElement | null;
    const shouldExpand = !this.isProviderExpanded(providerKey);
    const nextState: Record<string, boolean> = {};

    for (const key of this.groupedAvailableModels.map(group => group.key)) {
      nextState[key] = false;
    }

    if (shouldExpand) {
      nextState[providerKey] = true;
      requestAnimationFrame(() => {
        headerElement?.scrollIntoView({
          block: 'start',
          inline: 'nearest',
          behavior: 'smooth'
        });
      });
    }

    this.providerExpansionState = nextState;
  }

  getModelSecondaryText(model: ModelOption): string {
    const metadata: string[] = [];

    if (model.provider) {
      metadata.push(this.getProviderLabel(model.provider));
    } else if (model.category) {
      metadata.push(model.category);
    }

    if (model.contextLength) {
      metadata.push(`${this.formatContextLength(model.contextLength)} context`);
    }

    if (this.isDeprecatedModel(model)) {
      metadata.push('Deprecated');
    }

    return metadata.join(' · ');
  }

  getModelBadges(model: ModelOption): string[] {
    const badges: string[] = [];

    if (model.provider) {
      badges.push(this.getProviderLabel(model.provider));
    }

    if (model.supportsTools) {
      badges.push('Agent-ready');
    } else {
      badges.push('Ask only');
    }

    if (this.supportsImageOutput(model)) {
      badges.push('Image');
    }

    if (this.supportsAudioOutput(model)) {
      badges.push('Audio');
    }

    if (this.supportsVideoOutput(model)) {
      badges.push('Video');
    }

    if (model.contextLength) {
      badges.push(`${this.formatContextLength(model.contextLength)} ctx`);
    }

    const priceLabel = this.getPriceLabel(model);
    if (priceLabel) {
      badges.push(priceLabel);
    }

    return badges;
  }

  getModelPricingText(model: ModelOption): string {
    if (model.promptPrice === undefined || model.completionPrice === undefined) {
      return 'Pricing unavailable';
    }

    const blendedPrice = this.getBlendedPricePerMillion(model);
    if (blendedPrice === undefined) {
      return 'Pricing unavailable';
    }

    return `Blend ${this.activePriceBlendPreset.ratioLabel} ${this.formatPerMillionValue(blendedPrice)} / 1M tokens · Input ${this.formatPricePerMillion(model.promptPrice)} / 1M · Output ${this.formatPricePerMillion(model.completionPrice)} / 1M`;
  }

  getModelCompactPricingText(model: ModelOption): string {
    if (model.promptPrice === undefined || model.completionPrice === undefined) {
      return 'Pricing unavailable';
    }

    const blendedPrice = this.getBlendedPricePerMillion(model);
    if (blendedPrice === undefined) {
      return 'Pricing unavailable';
    }

    return `${this.activePriceBlendPreset.ratioLabel} ${this.formatPerMillionValue(blendedPrice)} · ${this.formatPricePerMillion(model.promptPrice)} in · ${this.formatPricePerMillion(model.completionPrice)} out`;
  }

  getModelTooltipText(model: ModelOption): string {
    const metadata = `${this.getModelSecondaryText(model)} · ${this.getModelPricingText(model)}`;
    return model.description ? `${model.description} ${metadata}` : metadata;
  }

  getRecommendedModelMeta(entry: RecommendedModelEntry): string {
    return `${entry.providerLabel} · ${entry.summary}`;
  }

  getProviderAvatar(model: ModelOption): string {
    return this.getProviderAvatarForProvider(model.provider || model.category);
  }

  getProviderAvatarForProvider(providerKey: string): string {
    const overrides: Record<string, string> = { 'x-ai': 'X', openai: 'O', deepseek: 'D' };
    if (overrides[providerKey]) return overrides[providerKey];
    const provider = this.getProviderLabel(providerKey);
    const compact = provider.replace(/[^A-Z0-9]/g, '');
    return compact.slice(0, 2);
  }

  getProviderBadgeStyle(model: ModelOption): Record<string, string> {
    const palette: Record<string, string> = {
      openai: 'linear-gradient(135deg, #0f766e, #14b8a6)',
      anthropic: 'linear-gradient(135deg, #9a3412, #f97316)',
      google: 'linear-gradient(135deg, #1d4ed8, #60a5fa)',
      deepseek: 'linear-gradient(135deg, #4338ca, #818cf8)',
      'x-ai': 'linear-gradient(135deg, #4b5563, #e5e7eb)',
      'meta-llama': 'linear-gradient(135deg, #7c2d12, #fb7185)',
      mistralai: 'linear-gradient(135deg, #a16207, #facc15)',
      qwen: 'linear-gradient(135deg, #0f766e, #2dd4bf)',
      moonshotai: 'linear-gradient(135deg, #7c3aed, #c084fc)',
      amazon: 'linear-gradient(135deg, #b45309, #f59e0b)'
    };

    const provider = model.provider || 'openrouter';

    return {
      background: palette[provider] || 'linear-gradient(135deg, #374151, #6b7280)',
      color: '#ffffff'
    };
  }

  getProviderBadgeStyleForProvider(providerKey: string): Record<string, string> {
    return this.getProviderBadgeStyle({
      key: providerKey,
      displayName: providerKey,
      category: providerKey,
      provider: providerKey
    });
  }

  isModelSelectable(model: ModelOption): boolean {
    if (this.currentMode !== 'agent') {
      return true;
    }

    return model.supportsTools !== false;
  }

  supportsImageOutput(model: ModelOption): boolean {
    return (model.outputModalities ?? []).includes('image');
  }

  supportsAudioOutput(model: ModelOption): boolean {
    return (model.outputModalities ?? []).includes('audio');
  }

  supportsVideoOutput(model: ModelOption): boolean {
    return (model.outputModalities ?? []).includes('video');
  }

  formatContextTokenCount(value: number): string {
    if (!Number.isFinite(value)) {
      return 'Unknown';
    }

    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }

    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }

    return `${Math.round(value)}`;
  }

  private formatContextLength(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }

    if (value >= 1000) {
      return `${Math.round(value / 1000)}K`;
    }

    return `${value}`;
  }

  private formatPricePerMillion(value: number): string {
    const normalized = (value * 1000000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return `$${normalized}`;
  }

  private formatPerMillionValue(value: number): string {
    const normalized = value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return `$${normalized}`;
  }

  getProviderLabel(provider: string): string {
    const labels: Record<string, string> = {
      openai: 'OpenAI',
      anthropic: 'Anthropic',
      google: 'Google',
      deepseek: 'DeepSeek',
      'x-ai': 'xAI',
      'meta-llama': 'Meta Llama',
      mistralai: 'Mistral',
      qwen: 'Qwen',
      moonshotai: 'Moonshot',
      amazon: 'Amazon Nova'
    };

    return labels[provider] || provider.replace(/[-_]/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  private getPriceLabel(model: ModelOption): string | null {
    const blendedPerMillion = this.getBlendedPricePerMillion(model);
    if (blendedPerMillion === undefined) {
      return null;
    }

    if (blendedPerMillion <= 1) {
      return 'Low cost';
    }

    if (blendedPerMillion <= 5) {
      return 'Low cost';
    }

    if (blendedPerMillion <= 20) {
      return 'Balanced';
    }

    return 'High power';
  }

  private getComparablePrice(model: ModelOption): number | undefined {
    if (model.promptPrice === undefined || model.completionPrice === undefined) {
      return undefined;
    }

    const outputShare = 1 - this.activePriceBlendPreset.inputShare;
    return (model.promptPrice * this.activePriceBlendPreset.inputShare) + (model.completionPrice * outputShare);
  }

  private getBlendedPricePerMillion(model: ModelOption): number | undefined {
    const comparablePrice = this.getComparablePrice(model);
    return comparablePrice !== undefined ? comparablePrice * 1000000 : undefined;
  }

  private getModelIdentityKey(model: ModelOption): string {
    return model.canonicalSlug || model.key;
  }

  private getModelLookupText(model: ModelOption): string {
    return [model.key, model.canonicalSlug, model.displayName, ...(model.aliasKeys ?? [])]
      .filter((value): value is string => !!value)
      .join(' ')
      .toLowerCase();
  }

  private getRecommendedProviderModels(providerModels: ModelOption[]): ModelOption[] {
    if (this.currentMode !== 'agent') {
      return providerModels;
    }

    return providerModels.filter(model => this.isModelSelectable(model));
  }

  private matchesTierDefinition(model: ModelOption, tierDefinition: ProviderTierDefinition): boolean {
    const haystack = this.getModelLookupText(model);
    const matchesInclude = tierDefinition.includePatterns.some(pattern => pattern.test(haystack));

    if (!matchesInclude) {
      return false;
    }

    return !(tierDefinition.excludePatterns ?? []).some(pattern => pattern.test(haystack));
  }

  private getPatternMatchScore(model: ModelOption, patterns?: RegExp[]): number {
    if (!patterns || patterns.length === 0) {
      return 0;
    }

    const haystack = this.getModelLookupText(model);
    return patterns.reduce((score, pattern) => score + (pattern.test(haystack) ? 1 : 0), 0);
  }

  private parseVersionParts(value: string): number[] {
    return (value.match(/\d+/g) || []).map(part => Number(part)).filter(part => Number.isFinite(part));
  }

  private compareVersionParts(left?: number[], right?: number[]): number {
    if (left && !right) {
      return -1;
    }

    if (!left && right) {
      return 1;
    }

    if (!left && !right) {
      return 0;
    }

    const maxLength = Math.max(left?.length || 0, right?.length || 0);
    for (let index = 0; index < maxLength; index += 1) {
      const leftPart = left?.[index] || 0;
      const rightPart = right?.[index] || 0;
      if (leftPart !== rightPart) {
        return rightPart - leftPart;
      }
    }

    return 0;
  }

  private extractVersionParts(model: ModelOption, patterns?: RegExp[]): number[] | undefined {
    if (!patterns || patterns.length === 0) {
      return undefined;
    }

    const haystack = this.getModelLookupText(model);
    let bestParts: number[] | undefined;

    for (const pattern of patterns) {
      const match = haystack.match(pattern);
      const capturedVersion = match?.[1];

      if (!capturedVersion) {
        continue;
      }

      const versionParts = this.parseVersionParts(capturedVersion);
      if (versionParts.length === 0) {
        continue;
      }

      if (!bestParts || this.compareVersionParts(versionParts, bestParts) < 0) {
        bestParts = versionParts;
      }
    }

    return bestParts;
  }

  private buildMissingTierReason(providerLabel: string, tierDefinition: ProviderTierDefinition, reason: 'no-match' | 'price-order' = 'no-match'): string {
    if (reason === 'price-order') {
      return `No clear current ${tierDefinition.label.toLowerCase()} candidate for ${providerLabel} without breaking price order.`;
    }

    return `No clear current ${tierDefinition.label.toLowerCase()} candidate for ${providerLabel}.`;
  }

  private isPreviewModel(model: ModelOption): boolean {
    return /(preview|beta|alpha|experimental)/i.test(this.getModelLookupText(model));
  }

  private getDeprecationTimestamp(model: ModelOption): number | undefined {
    const deprecationDate = model.expirationDate ?? model.deprecationDate;

    if (deprecationDate === undefined || deprecationDate === null || deprecationDate === '') {
      return undefined;
    }

    if (typeof deprecationDate === 'number' && Number.isFinite(deprecationDate)) {
      return deprecationDate > 1000000000000 ? deprecationDate : deprecationDate * 1000;
    }

    const parsedNumeric = Number(deprecationDate);
    if (Number.isFinite(parsedNumeric) && parsedNumeric > 0) {
      return parsedNumeric > 1000000000000 ? parsedNumeric : parsedNumeric * 1000;
    }

    const parsedDate = Date.parse(String(deprecationDate));
    return Number.isFinite(parsedDate) ? parsedDate : undefined;
  }

  private isDeprecatedModel(model: ModelOption): boolean {
    const deprecationTimestamp = this.getDeprecationTimestamp(model);
    return deprecationTimestamp !== undefined && deprecationTimestamp <= Date.now();
  }

  private compareModelFreshness(left: ModelOption, right: ModelOption): number {
    const leftCreated = left.created ?? 0;
    const rightCreated = right.created ?? 0;

    if (leftCreated !== rightCreated) {
      return rightCreated - leftCreated;
    }

    return this.getModelIdentityKey(left).localeCompare(this.getModelIdentityKey(right));
  }

  private compareTierCandidates(left: ModelOption, right: ModelOption, tierDefinition: ProviderTierDefinition): number {
    const leftPreferred = this.getPatternMatchScore(left, tierDefinition.preferPatterns);
    const rightPreferred = this.getPatternMatchScore(right, tierDefinition.preferPatterns);

    if (leftPreferred !== rightPreferred) {
      return rightPreferred - leftPreferred;
    }

    const leftDeprecated = this.isDeprecatedModel(left) ? 1 : 0;
    const rightDeprecated = this.isDeprecatedModel(right) ? 1 : 0;

    if (leftDeprecated !== rightDeprecated) {
      return leftDeprecated - rightDeprecated;
    }

    const leftPreview = !tierDefinition.allowPreview && tierDefinition.key !== 'best' && this.isPreviewModel(left) ? 1 : 0;
    const rightPreview = !tierDefinition.allowPreview && tierDefinition.key !== 'best' && this.isPreviewModel(right) ? 1 : 0;

    if (leftPreview !== rightPreview) {
      return leftPreview - rightPreview;
    }

    const versionComparison = this.compareVersionParts(
      this.extractVersionParts(left, tierDefinition.versionPatterns),
      this.extractVersionParts(right, tierDefinition.versionPatterns)
    );

    if (versionComparison !== 0) {
      return versionComparison;
    }

    const freshnessCompare = this.compareModelFreshness(left, right);
    if (freshnessCompare !== 0) {
      return freshnessCompare;
    }

    if (tierDefinition.key === 'good') {
      const leftPrice = this.getComparablePrice(left) ?? Number.POSITIVE_INFINITY;
      const rightPrice = this.getComparablePrice(right) ?? Number.POSITIVE_INFINITY;

      if (leftPrice !== rightPrice) {
        return leftPrice - rightPrice;
      }
    }

    if (tierDefinition.key === 'better') {
      const leftPrice = this.getComparablePrice(left) ?? Number.POSITIVE_INFINITY;
      const rightPrice = this.getComparablePrice(right) ?? Number.POSITIVE_INFINITY;

      if (leftPrice !== rightPrice) {
        return leftPrice - rightPrice;
      }
    }

    return this.compareProviderModels(left, right);
  }

  private getTierCandidates(
    providerModels: ModelOption[],
    tierDefinition: ProviderTierDefinition,
    excludedKeys: Set<string>
  ): ModelOption[] {
    const remainingModels = providerModels.filter(model => !excludedKeys.has(this.getModelIdentityKey(model)));
    const matchingActiveModels = remainingModels.filter(model => !this.isDeprecatedModel(model) && this.matchesTierDefinition(model, tierDefinition));

    if (matchingActiveModels.length > 0) {
      return [...matchingActiveModels].sort((left, right) => this.compareTierCandidates(left, right, tierDefinition));
    }

    const matchingModels = remainingModels.filter(model => this.matchesTierDefinition(model, tierDefinition));
    return [...matchingModels].sort((left, right) => this.compareTierCandidates(left, right, tierDefinition));
  }

  private buildCuratedModelEntry(
    providerModels: ModelOption[],
    tierDefinition: ProviderTierDefinition,
    excludedKeys: Set<string>,
    minimumComparablePrice?: number,
    providerLabel?: string
  ): { entry?: { model: ModelOption; tierKey: CuratedTierKey; tier: string; summary: string }; missingReason?: string } {
    const recommendedProviderModels = this.getRecommendedProviderModels(providerModels);

    if (recommendedProviderModels.length === 0) {
      return {
        missingReason: this.buildMissingTierReason(providerLabel || 'this provider', tierDefinition)
      };
    }

    const fallbackProviderModels = recommendedProviderModels.filter(model =>
      !(tierDefinition.excludePatterns ?? []).some(pattern => pattern.test(this.getModelLookupText(model)))
    );

    const matchesPriceOrder = (model: ModelOption): boolean => {
      if (minimumComparablePrice === undefined) {
        return true;
      }

      const comparablePrice = this.getComparablePrice(model);
      return comparablePrice !== undefined && comparablePrice >= minimumComparablePrice;
    };

    const fallbackModel = tierDefinition.allowFallback === false
      ? undefined
      : this.pickFallbackTierModel(fallbackProviderModels.filter(matchesPriceOrder), tierDefinition.key, excludedKeys);

    const tierCandidates = this.getTierCandidates(recommendedProviderModels, tierDefinition, excludedKeys);
    const orderedTierCandidates = tierCandidates.filter(matchesPriceOrder);

    const model = orderedTierCandidates[0]
      || fallbackModel;

    if (!model) {
      const priceOrderBlocked = tierCandidates.length > 0 && orderedTierCandidates.length === 0;
      return {
        missingReason: this.buildMissingTierReason(providerLabel || 'this provider', tierDefinition, priceOrderBlocked ? 'price-order' : 'no-match')
      };
    }

    excludedKeys.add(this.getModelIdentityKey(model));

    return {
      entry: {
        model,
        tierKey: tierDefinition.key,
        tier: tierDefinition.label,
        summary: tierDefinition.summary
      }
    };
  }

  private enrichEntry(raw: { model: ModelOption; tierKey?: CuratedTierKey; tier: string; summary: string }): ModelGroupEntry {
    return {
      ...raw,
      badgeStyle: this.getProviderBadgeStyle(raw.model),
      avatar: this.getProviderAvatar(raw.model),
      tooltipText: this.getModelTooltipText(raw.model),
      secondaryText: this.getModelSecondaryText(raw.model),
      pricingText: this.getModelPricingText(raw.model),
      compactPricingText: this.getModelCompactPricingText(raw.model),
      badges: this.getModelBadges(raw.model),
      selectable: this.isModelSelectable(raw.model)
    };
  }

  private buildProviderGroup(provider: ProviderModelDefinition, labelOverride?: string, descriptionOverride?: string): ModelGroup | undefined {
    const providerModels = this.getLogicalProviderModels(provider.key);

    if (providerModels.length === 0) {
      return undefined;
    }

    const excludedKeys = new Set<string>();
    const missingTiers: Array<{ tier: string; reason: string }> = [];
    let minimumComparablePrice: number | undefined;
    const recommendedEntries = provider.includeInRecommended === false
      ? []
      : provider.tiers.reduce<ModelGroupEntry[]>((entries, tierDefinition) => {
        const selection = this.buildCuratedModelEntry(
          providerModels,
          tierDefinition,
          excludedKeys,
          minimumComparablePrice,
          labelOverride || provider.label
        );

        if (selection.entry) {
          const enrichedEntry = this.enrichEntry(selection.entry);
          const comparablePrice = this.getComparablePrice(selection.entry.model);

          if (comparablePrice !== undefined) {
            minimumComparablePrice = comparablePrice;
          }

          entries.push(enrichedEntry);
        } else if (selection.missingReason) {
          missingTiers.push({ tier: tierDefinition.label, reason: selection.missingReason });
        }

        return entries;
      }, []);

    const recommendedKeys = new Set(recommendedEntries.map(entry => this.getModelIdentityKey(entry.model)));
    const additionalEntries = providerModels
      .filter(model => recommendedEntries.length === 0 || !recommendedKeys.has(this.getModelIdentityKey(model)))
      .sort((left, right) => this.compareProviderModels(left, right))
      .map(model => this.enrichEntry({
        model,
        tier: model.key === this.currentModelKey ? 'Current' : 'More',
        summary: model.key === this.currentModelKey ? 'Current thread model' : 'Available option'
      }));

    return {
      key: provider.key,
      label: labelOverride || provider.label,
      description: descriptionOverride || provider.description,
      count: providerModels.length,
      recommendedEntries,
      missingTiers,
      additionalEntries,
      badgeStyle: this.getProviderBadgeStyleForProvider(provider.key),
      avatar: this.getProviderAvatarForProvider(provider.key)
    };
  }

  private buildRecommendedModelEntries(): RecommendedModelEntry[] {
    const includedProviders = this.providerRecommendations.length > 0
      ? new Set(this.providerRecommendations.map(provider => provider.provider))
      : new Set(
        this.providerDefinitions
          .filter(provider => provider.includeInRecommended !== false)
          .map(provider => provider.key)
      );

    return this.groupedAvailableModels
      .filter(group => includedProviders.has(group.key))
      .flatMap(group =>
      group.recommendedEntries.map(entry => ({
        providerKey: group.key,
        providerLabel: group.label,
        providerDescription: group.description,
        model: entry.model,
        tierKey: entry.tierKey as CuratedTierKey,
        tier: entry.tier,
        summary: entry.summary,
        blendedPrice: this.getComparablePrice(entry.model),
        badgeStyle: entry.badgeStyle,
        avatar: entry.avatar,
        tooltipText: entry.tooltipText,
        secondaryText: entry.secondaryText,
        pricingText: entry.pricingText,
        badges: entry.badges,
        selectable: entry.selectable,
        meta: `${group.label} · ${entry.summary}`
      }))
    );
  }

  private getLogicalProviderModels(providerKey: string): ModelOption[] {
    return this.availableModels
      .filter(model => (model.provider || model.key.split('/')[0]) === providerKey)
      .filter(model => this.isLogicalChatModel(model));
  }

  private isLogicalChatModel(model: ModelOption): boolean {
    const outputModalities = model.outputModalities ?? ['text'];
    if (outputModalities.length > 0 && !outputModalities.includes('text')) {
      return false;
    }

    if (this.isDeprecatedModel(model)) {
      return false;
    }

    const haystack = `${model.key} ${model.displayName}`.toLowerCase();
    return !/(audio-preview|audio preview|embed|embedding|guard|moderation|transcribe|tts|rerank|realtime|speech|:free)/.test(haystack);
  }

  private compareProviderModels(left: ModelOption, right: ModelOption): number {
    const leftScore = this.scoreProviderModel(left);
    const rightScore = this.scoreProviderModel(right);

    if (leftScore !== rightScore) {
      return rightScore - leftScore;
    }

    const freshnessCompare = this.compareModelFreshness(left, right);
    if (freshnessCompare !== 0) {
      return freshnessCompare;
    }

    return left.displayName.localeCompare(right.displayName);
  }

  private scoreProviderModel(model: ModelOption): number {
    let score = 0;

    if (model.key === this.currentModelKey) {
      score += 1000;
    }

    if (model.supportsTools) {
      score += 100;
    }

    if (!this.isDeprecatedModel(model)) {
      score += 40;
    }

    if (!this.isPreviewModel(model)) {
      score += 25;
    }

    if (model.created) {
      score += 10;
    }

    if ((model.outputModalities ?? []).includes('text')) {
      score += 5;
    }

    return score;
  }

  get hasThread(): boolean {
    return !!this.currentThread || this.messageCount > 0;
  }

  ngOnInit(): void {
    this.loadPlaybooks();
    this.loadAgencySkills();
    this.hydratePlaybookPrefillFromInputs();
    this.refreshGroupedAvailableModels();
    this.refreshGroupedAvailableTools();
    this.ensureProviderExpansionState();
    this.ensureToolGroupExpansionState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availableModels'] || changes['currentModelKey'] || changes['currentMode'] || changes['providerRecommendations']) {
      this.refreshGroupedAvailableModels();
    }

    if (changes['modelCatalogStatus'] && changes['modelCatalogStatus'].currentValue !== 'loading') {
      this.optimisticModelCatalogLoading = false;
    }

    if (changes['toolCatalogStatus'] && changes['toolCatalogStatus'].currentValue !== 'loading') {
      this.optimisticToolCatalogLoading = false;
    }

    if ((changes['availableModels'] && !changes['availableModels'].firstChange) ||
        (changes['currentModelKey'] && !changes['currentModelKey'].firstChange)) {
      this.ensureProviderExpansionState();
    }

    if (changes['availableTools'] || changes['selectedToolNames']) {
      this.refreshGroupedAvailableTools();
      this.ensureToolGroupExpansionState();
    }

    // Reload playbooks if agent changes
    if (changes['agent'] && !changes['agent'].firstChange) {
      this.loadPlaybooks();
    }

    if (changes['playbookPrefillSlug'] || changes['playbookPrefillFieldValues']) {
      this.hydratePlaybookPrefillFromInputs();
    }

    if (changes['agencyId'] && !changes['agencyId'].firstChange) {
      this.loadAgencySkills();
    }

    if (changes['attachedImageItems']) {
      this.hydrateImageAttachments(this.attachedImageItems);
      this.scheduleAttachmentLayoutMeasurement();
    }

    if (changes['attachedFileItems']) {
      this.hydrateFileAttachments(this.attachedFileItems);
      this.scheduleAttachmentLayoutMeasurement();
    }

    if (changes['attachedItems'] || changes['currentModelLabel'] || changes['currentModelKey'] || changes['agent']) {
      this.scheduleAttachmentLayoutMeasurement();
    }

    if (changes['newMessage'] && !changes['newMessage'].firstChange) {
      this.queueTextareaResize();
    }

    if (changes['threadAssetItems']) {
      this.syncThreadAssetFilterSelection();
    }
  }

  ngOnDestroy(): void {
    this.hideModelInfoTooltip(true);
    this.skillsSubscription?.unsubscribe();
    this.contextItemSubscriptions.forEach(s => s.unsubscribe());
    this.contextFieldItemSubs.forEach(s => s.unsubscribe());
    this.attachmentLayoutObserver?.disconnect();
    if (this.attachmentLayoutFrameId !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.attachmentLayoutFrameId);
    }
    this.clearAttachments();
  }

  ngAfterViewInit(): void {
    // Immediate resize to prevent jank on first paint
    this.autoResizeTextarea();
    // Also queue a follow-up in case layout isn't settled yet
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => this.autoResizeTextarea());
    }
    if (this.autofocusOnLoad) {
      this.focusInput();
    }

    this.setupAttachmentLayoutObserver();
    this.scheduleAttachmentLayoutMeasurement();
  }

  private ensureProviderExpansionState(): void {
    const providerKeys = this.visibleGroupedAvailableModels.map(group => group.key);
    const nextState: Record<string, boolean> = {};

    for (const key of providerKeys) {
      nextState[key] = this.providerExpansionState[key] ?? false;
    }

    const expandedKey = providerKeys.find(key => nextState[key]);
    if (expandedKey) {
      for (const key of providerKeys) {
        nextState[key] = key === expandedKey;
      }
    }

    this.providerExpansionState = nextState;
  }

  private refreshGroupedAvailableModels(): void {
    this.groupedAvailableModels = this.buildGroupedAvailableModels();
    this.refreshVisibleGroupedAvailableModels();
    this.recommendedModelEntries = this.buildRecommendedModelEntries();
    this.refreshSortedRecommendedEntries();
  }

  private refreshGroupedAvailableTools(): void {
    this.groupedAvailableTools = this.buildGroupedAvailableTools();
  }

  private refreshVisibleGroupedAvailableModels(): void {
    this.visibleGroupedAvailableModels = this.groupedAvailableModels;
  }

  private refreshSortedRecommendedEntries(): void {
    const direction = this.recommendedSortDirection === 'asc' ? 1 : -1;
    this.sortedRecommendedModelEntries = [...this.recommendedModelEntries]
      .sort((left, right) => {
        const leftPrice = left.blendedPrice ?? Number.POSITIVE_INFINITY;
        const rightPrice = right.blendedPrice ?? Number.POSITIVE_INFINITY;

        if (leftPrice !== rightPrice) {
          return (leftPrice - rightPrice) * direction;
        }

        return left.providerLabel.localeCompare(right.providerLabel);
      });
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    this.autoResizeTextarea();
    this.messageChanged.emit({
      value: target.value,
      event: event
    });
  }

  onInputKeyDown(event: KeyboardEvent): void {
    this.inputKeyDown.emit({
      event,
      inputType: 'input'
    });
  }

  onTextareaKeyDown(event: KeyboardEvent): void {
    if (this.handleSkillTokenDeletion(event)) {
      return;
    }

    if (this.shouldOpenSkillsModalForSlash(event)) {
      this.queueSlashSkillsModal();
    }

    this.textareaKeyDown.emit({
      event,
      inputType: 'textarea'
    });
  }

  onTextareaPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (!clipboardData) {
      return;
    }

    const imageFiles = Array.from(clipboardData.items || [])
      .filter(item => item.kind === 'file' && item.type.toLowerCase().startsWith('image/'))
      .map(item => item.getAsFile())
      .filter((file): file is File => !!file);

    if (imageFiles.length === 0) {
      return;
    }

    if (!Array.from(clipboardData.types || []).includes('text/plain')) {
      event.preventDefault();
    }

    void this.attachImageFiles(imageFiles);
    this.imageFilesPasted.emit(imageFiles);
  }

  onImageFileInputChange(event: Event): void {
    const files = this.getFilesFromInputEvent(event);
    console.log('[chat-attachments] image input changed', {
      fileCount: files.length,
      fileNames: files.map(file => file.name)
    });
    if (files.length > 0) {
      console.log('[chat-attachments] forwarding image files to chat component', {
        fileCount: files.length,
        fileNames: files.map(file => file.name)
      });
      void this.attachImageFiles(files);
      this.imageFilesSelectedHandler?.(files);
      this.imageFilesSelected.emit(files);
    }
  }

  onFileInputChange(event: Event): void {
    const files = this.getFilesFromInputEvent(event);
    console.log('[chat-attachments] file input changed', {
      fileCount: files.length,
      fileNames: files.map(file => file.name)
    });
    if (files.length > 0) {
      console.log('[chat-attachments] forwarding generic files to chat component', {
        fileCount: files.length,
        fileNames: files.map(file => file.name)
      });
      void this.attachFiles(files);
      this.filesSelectedHandler?.(files);
      this.filesSelected.emit(files);
    }
  }

  onAttachedContextRemoved(item: AttachedContextItem): void {
    this.removeAttachedContext.emit(item);
    this.scheduleAttachmentLayoutMeasurement();
  }

  onAttachedImageRemoved(item: ChatImageAttachment): void {
    this.removeImageAttachment(item.id);
    this.removeAttachedImage.emit(item);
    this.scheduleAttachmentLayoutMeasurement();
  }

  onAttachedFileRemoved(item: ChatFileAttachment): void {
    this.removeFileAttachment(item.id);
    this.removeAttachedFile.emit(item);
    this.scheduleAttachmentLayoutMeasurement();
  }

  get hasReadyImageAttachments(): boolean {
    return this.composerAttachedImageItems.some(item => item.status === 'ready');
  }

  get hasReadyFileAttachments(): boolean {
    return this.composerAttachedFileItems.some(item => item.status === 'ready');
  }

  get hasPendingImageAttachments(): boolean {
    return this.composerAttachedImageItems.some(item => item.status === 'uploading');
  }

  get hasPendingFileAttachments(): boolean {
    return this.composerAttachedFileItems.some(item => item.status === 'uploading');
  }

  get hasFailedImageAttachments(): boolean {
    return this.composerAttachedImageItems.some(item => item.status === 'error');
  }

  get hasFailedFileAttachments(): boolean {
    return this.composerAttachedFileItems.some(item => item.status === 'error');
  }

  get displayImageAttachmentValidationMessage(): string {
    if (this.imageAttachmentValidationMessage) {
      return this.imageAttachmentValidationMessage;
    }

    if (this.hasPendingImageAttachments || this.hasPendingFileAttachments) {
      return 'Uploading attachments before send.';
    }

    if (this.hasFailedImageAttachments || this.hasFailedFileAttachments) {
      return 'Remove failed attachments before sending.';
    }

    if (!this.imageAttachmentsSupported && this.hasReadyImageAttachments) {
      return 'The selected model does not support image input. Choose a multimodal model.';
    }

    return '';
  }

  get canSendCurrentMessage(): boolean {
    if (this.activePlaybook) {
      return this.canSubmitPlaybook;
    }

    if (this.isLoadingOrInitializing || this.hasPendingImageAttachments || this.hasFailedImageAttachments || this.hasPendingFileAttachments || this.hasFailedFileAttachments) {
      return false;
    }

    if (!this.imageAttachmentsSupported && this.hasReadyImageAttachments) {
      return false;
    }

    return !!this.newMessage?.trim() || this.hasReadyImageAttachments || this.hasReadyFileAttachments;
  }

  getReadyImageAttachments(): ChatImageAttachment[] {
    return this.composerAttachedImageItems
      .filter(item => item.status === 'ready' && !!item.url)
      .map(item => ({ ...item }));
  }

  getReadyFileAttachments(): ChatFileAttachment[] {
    return this.composerAttachedFileItems
      .filter(item => item.status === 'ready' && !!item.url)
      .map(item => ({ ...item }));
  }

  clearAttachments(): void {
    for (const attachment of this.composerAttachedImageItems) {
      if (attachment.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    }

    this.composerAttachedImageItems = [];
    this.composerAttachedFileItems = [];
    this.cdr.markForCheck();
  }

  getAttachedImagePreviewUrl(item: ChatImageAttachment): string {
    return item.previewUrl || item.url;
  }

  getAttachedImageStatusLabel(item: ChatImageAttachment): string {
    if (item.status === 'uploading') {
      return item.uploadProgress && item.uploadProgress > 0 ? `Uploading ${item.uploadProgress}%` : 'Uploading';
    }

    if (item.status === 'error') {
      return item.errorMessage || 'Upload failed';
    }

    return item.mimeType.replace('image/', '').toUpperCase();
  }

  getAttachedImageProgressValue(item: ChatImageAttachment): number {
    return Math.max(0, Math.min(100, Math.round(item.uploadProgress || 0)));
  }

  getAttachedFileStatusLabel(item: ChatFileAttachment): string {
    if (item.status === 'uploading') {
      return item.uploadProgress && item.uploadProgress > 0 ? `Uploading ${item.uploadProgress}%` : 'Uploading';
    }

    if (item.status === 'error') {
      return item.errorMessage || 'Upload failed';
    }

    return this.formatFileMeta(item);
  }

  getAttachedFileProgressValue(item: ChatFileAttachment): number {
    return Math.max(0, Math.min(100, Math.round(item.uploadProgress || 0)));
  }

  private formatFileMeta(item: ChatFileAttachment): string {
    const sizeText = typeof item.sizeBytes === 'number' ? this.formatBytes(item.sizeBytes) : '';
    if (!sizeText) {
      return 'Ready';
    }

    return sizeText;
  }

  private formatBytes(value: number): string {
    if (value >= 1024 * 1024) {
      return `${(value / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
    }

    if (value >= 1024) {
      return `${Math.round(value / 1024)} KB`;
    }

    return `${value} B`;
  }

  openAttachedPopover(event: MouseEvent): void {
    event.stopPropagation();

    const trigger = event.currentTarget as HTMLElement | null;
    if (!trigger || !this.attachmentPopoverTemplate) {
      return;
    }

    this.popoverService.open({
      element: trigger,
      placement: 'bottom-left',
      align: 'start',
      bodyTemplate: this.attachmentPopoverTemplate,
      noPadding: true,
      backdrop: true,
      backdropDismiss: true,
      displayMode: this.theme.isLight ? 'light' : 'dark',
      dimensions: { width: 260 }
    });
  }

  closeAttachedPopover(): void {
    this.popoverService.close();
  }

  getComposerAttachmentTitle(item: ComposerAttachmentListItem): string {
    return item.label;
  }

  onUnifiedAttachmentRemoved(item: ComposerAttachmentListItem): void {
    const nextCount = this.totalComposerAttachmentCount - 1;
    if (nextCount <= 0) {
      this.closeAttachedPopover();
    }

    if (item.type === 'context') {
      this.onAttachedContextRemoved(item.item as AttachedContextItem);
      return;
    }

    if (item.type === 'image') {
      this.onAttachedImageRemoved(item.item as ChatImageAttachment);
      return;
    }

    this.onAttachedFileRemoved(item.item as ChatFileAttachment);
  }

  getAttachedDisplayName(item: AttachedContextItem): string {
    const name = item.displayName || '';
    if (!name || name.toLowerCase().startsWith('untitled')) {
      const typeLabels: Record<string, string> = {
        business: 'Business',
        partner: 'Partner',
        funnel: 'Funnel',
        page: 'Page',
        form: 'Form',
        note: 'Note',
        plan: 'Plan',
        proposal: 'Proposal',
        task: 'Task',
        ticket: 'Ticket',
        app: 'App',
        media: 'Media',
        file: 'File',
        selection: 'Selection'
      };
      return typeLabels[item.type] || item.type;
    }
    return name;
  }

  getAttachedContextIconClass(item: AttachedContextItem): string {
    const icon = (item.icon || '').trim();

    if (!icon) {
      return 'fa-regular fa-paperclip';
    }

    if (icon.includes('fa-regular') || icon.includes('fa-solid') || icon.includes('fas ') || icon.includes('far ')) {
      return icon;
    }

    return `fa-regular ${icon}`;
  }

  private setupAttachmentLayoutObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const toolbarLeft = this.toolbarLeft?.nativeElement;
    const toolbarLeading = this.toolbarLeading?.nativeElement;
    if (!toolbarLeft || !toolbarLeading) {
      return;
    }

    this.attachmentLayoutObserver?.disconnect();
    this.attachmentLayoutObserver = new ResizeObserver(() => {
      this.scheduleAttachmentLayoutMeasurement();
    });
    this.attachmentLayoutObserver.observe(toolbarLeft);
    this.attachmentLayoutObserver.observe(toolbarLeading);
  }

  private scheduleAttachmentLayoutMeasurement(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.attachmentLayoutFrameId !== null) {
      window.cancelAnimationFrame(this.attachmentLayoutFrameId);
    }

    this.attachmentLayoutFrameId = window.requestAnimationFrame(() => {
      this.attachmentLayoutFrameId = null;
      this.recalculateAttachmentGrouping();
    });
  }

  private recalculateAttachmentGrouping(): void {
    const attachments = this.allComposerAttachmentItems;
    const toolbarLeftWidth = this.toolbarLeft?.nativeElement.clientWidth || 0;
    const toolbarLeadingWidth = this.toolbarLeading?.nativeElement.scrollWidth || 0;

    if (attachments.length === 0 || toolbarLeftWidth === 0) {
      this.collapseComposerAttachments = false;
      this.closeAttachedPopover();
      this.cdr.markForCheck();
      return;
    }

    const availableWidth = Math.max(0, toolbarLeftWidth - toolbarLeadingWidth - 16);
    const requiredWidth = attachments.reduce((total, item, index) => {
      return total + this.estimateComposerAttachmentChipWidth(item) + (index > 0 ? 4 : 0);
    }, 0);

    const shouldGroup = attachments.length > 1 && requiredWidth > availableWidth;
    this.collapseComposerAttachments = shouldGroup;
    if (!shouldGroup) {
      this.closeAttachedPopover();
    }

    this.cdr.markForCheck();
  }

  private estimateComposerAttachmentChipWidth(item: ComposerAttachmentListItem): number {
    const baseWidth = item.type === 'context' ? 54 : 50;
    const characterWidth = 6.2;
    return Math.min(138, Math.max(64, baseWidth + (item.label.length * characterWidth)));
  }

  onComposerAction(action: 'prompts' | 'agent' | 'context' | 'settings' | 'details'): void {
    this.composerActionSelected.emit(action);
  }

  onAddClick(): void {
    console.log('[chat-attachments] opening add menu');
    this.openAddMenuModal();
    this.addMenuRequested.emit();
  }

  onMoreOptionsClick(): void {
    this.openMoreMenuModal();
  }

  onSearchClick(): void {
    const agencyId = this.authService.aid;
    if (!agencyId) return;

    this.actionsPaletteService.openWithConfigs({
      autoOpenIndexMode: true,
      indexModeDefaultTarget: 'agency-threads',
      indexModeDefaultParent: agencyId,
      indexModeDefaultParentType: 'agency',
      hideIndexModeSelector: true
    });
  }

  openThreadAssetsModal(): void {
    if (!this.showThreadAssetsButton) {
      return;
    }

    this.selectedThreadAssetFilter = 'all';
    this.threadAssetSearchQuery = '';
    this.focusedThreadAssetIndex = -1;
    this.openSelectorModal('assets');
  }

  onViewClick(): void {
    if (!this.canOpenViewModal) {
      return;
    }

    this.openViewModal();
  }

  onAgentChipClick(): void {
    this.openAgentPickerModal();
    this.agentChipClicked.emit();
  }

  onModeChipClick(): void {
    this.openModePickerModal();
    this.modeChipClicked.emit();
  }

  onModelChipClick(): void {
    // Only show loading state if catalog isn't already loaded
    if (this.modelCatalogStatus !== 'live') {
      this.optimisticModelCatalogLoading = true;
    }
    this.openModelPickerModal();
    this.modelChipClicked.emit();
  }

  openAddMenuModal(): void {
    this.openSelectorModal('add');
  }

  openMoreMenuModal(): void {
    this.openSelectorModal('more');
  }

  openPromptPickerModal(): void {
    this.showPromptPopover = true;
    this.openSelectorModal('prompts');
  }

  openAgentPickerModal(): void {
    this.openSelectorModal('agent');
  }

  openContextPickerModal(initialSource: ContextAttachmentSource = 'business'): void {
    this.contextPickerInitialSource = initialSource;
    this.showContextPopover = true;
    this.openSelectorModal('context');
  }

  openModePickerModal(): void {
    this.openSelectorModal('mode');
  }

  openModelPickerModal(): void {
    console.log('[model-picker] opening model modal');
      this.activeModelSettingsTab = 'default';
    this.openSelectorModal('model');
  }

  openToolPickerModal(): void {
    if (this.toolCatalogStatus !== 'live') {
      this.optimisticToolCatalogLoading = true;
    }
    if (this.selectedToolCount === 0) {
      this.collapseToolGroups();
    } else {
      this.ensureToolGroupExpansionState();
    }
    this.openSelectorModal('tools');
    this.toolSelectorOpened.emit();
  }

  openViewModal(): void {
    this.openSelectorModal('view');
  }

  openThreadDetailsModal(): void {
    this.showThreadDetailsPopover = true;
    this.openSelectorModal('details');
  }

  onThreadAssetSelectedFromModal(asset: ChatThreadAssetItem): void {
    this.pendingThreadAssetSelection = asset;
    this.closeSelectorModal();
  }

  onThreadAssetSearchInput(event: Event): void {
    this.threadAssetSearchQuery = String((event.target as HTMLInputElement | null)?.value || '');
    this.focusedThreadAssetIndex = -1;
    this.cdr.markForCheck();
  }

  onThreadAssetSearchFocus(): void {
    this.focusedThreadAssetIndex = -1;
  }

  onThreadAssetSearchKeyDown(event: KeyboardEvent): void {
    if (!this.filteredThreadAssetItems.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusThreadAssetByIndex(0);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusThreadAssetByIndex(this.filteredThreadAssetItems.length - 1);
    }
  }

  onThreadAssetItemFocus(index: number): void {
    this.focusedThreadAssetIndex = index;
  }

  onThreadAssetItemKeyDown(event: KeyboardEvent, index: number): void {
    if (!this.filteredThreadAssetItems.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusThreadAssetByIndex(Math.min(index + 1, this.filteredThreadAssetItems.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index === 0) {
        this.focusThreadAssetSearchField();
        return;
      }

      this.focusThreadAssetByIndex(index - 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.focusThreadAssetByIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.focusThreadAssetByIndex(this.filteredThreadAssetItems.length - 1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const asset = this.filteredThreadAssetItems[index];
      if (asset) {
        this.onThreadAssetSelectedFromModal(asset);
      }
    }
  }

  selectThreadAssetFilter(filter: ThreadAssetFilter): void {
    if (filter === this.selectedThreadAssetFilter) {
      return;
    }

    this.selectedThreadAssetFilter = filter;
    this.focusedThreadAssetIndex = -1;
    this.cdr.markForCheck();
  }

  isThreadAssetFilterSelected(filter: ThreadAssetFilter): boolean {
    return this.selectedThreadAssetFilter === filter;
  }

  trackThreadAsset(index: number, asset: ChatThreadAssetItem): string {
    return `${asset.kind}:${asset.id}:${index}`;
  }

  closeSelectorModal(): void {
    this.hideModelInfoTooltip(true);
    if (this.activeSelectorModal) {
      this.uiModalService.close();
      return;
    }

    this.resetSelectorState();
  }

  // Selector modal handlers
  selectAgentFromPopover(agent: AgentOption): void {
    this.agent = agent.value;
    this.closeSelectorModal();
    this.agentChanged.emit(agent.value);
    this.focusInput();
  }

  selectModeFromPopover(modeOption: { value: 'ask' | 'agent'; label: string }): void {
    this.currentMode = modeOption.value;
    this.closeSelectorModal();
    this.modeChanged.emit(modeOption.value);
    this.focusInput();
  }

  onModelPopoverBodyClick(event: MouseEvent): void {
    this.hideModelInfoTooltip();
    this.isPriceBlendSelectorOpen = false;
    event.stopPropagation();
  }

  onModelInfoClick(event: MouseEvent, tooltipText: string): void {
    event.stopPropagation();

    const element = event.currentTarget as HTMLElement | null;
    if (!element || !tooltipText) {
      return;
    }

    const isSameTooltip = this.activeModelInfoTooltipElement === element && this.activeModelInfoTooltipText === tooltipText;
    if (isSameTooltip) {
      this.hideModelInfoTooltip(true);
      return;
    }

    this.activeModelInfoTooltipElement = element;
    this.activeModelInfoTooltipText = tooltipText;

    this.tooltipService.show({
      element,
      content: tooltipText,
      placement: 'top-right',
      trigger: 'manual',
      delay: 0,
      dismissDelay: 0,
      persistent: false,
      suppressOnMobile: true,
      displayMode: this.theme.isLight ? 'light' : 'dark'
    });
  }

  selectModelFromPopover(model: ModelOption): void {
    if (!this.isModelSelectable(model)) {
      return;
    }

    this.hideModelInfoTooltip(true);
    this.isPriceBlendSelectorOpen = false;
    this.currentModelLabel = model.displayName;
    this.currentModelKey = model.key;
    this.closeSelectorModal();
    this.modelChanged.emit(model);
    this.focusInput();
  }

  clearToolSelection(): void {
    if (this.selectedToolCount === 0) {
      return;
    }

    this.selectedToolNames = [];
    this.refreshGroupedAvailableTools();
    this.ensureToolGroupExpansionState();
    this.toolSelectionChanged.emit([]);
    this.cdr.markForCheck();
  }

  applyDefaultToolSelection(): void {
    const nextSelection = normalizeToolNameList(this.defaultToolNames)
      .filter(name => this.availableTools.some(tool => tool.name === name));

    if (areToolSelectionsEqual(this.selectedToolNames, nextSelection)) {
      return;
    }

    this.selectedToolNames = nextSelection;
    this.refreshGroupedAvailableTools();
    this.ensureToolGroupExpansionState();
    this.toolSelectionChanged.emit(nextSelection);
    this.cdr.markForCheck();
  }

  toggleToolSelection(tool: ToolOption): void {
    const isSelected = this.isToolSelected(tool.name);
    const nextSelection = isSelected
      ? this.selectedToolNames.filter(name => name !== tool.name)
      : [...this.selectedToolNames, tool.name];

    this.selectedToolNames = nextSelection;
    this.refreshGroupedAvailableTools();
    this.ensureToolGroupExpansionState();
    this.toolSelectionChanged.emit(nextSelection);
    this.cdr.markForCheck();
  }

  isToolSelected(toolName: string): boolean {
    return this.selectedToolNames.includes(toolName);
  }

  isToolGroupExpanded(groupKey: string): boolean {
    return !!this.toolGroupExpansionState[groupKey];
  }

  toggleToolGroup(groupKey: string, event: Event): void {
    event.stopPropagation();
    const headerElement = event.currentTarget as HTMLElement | null;
    const nextExpanded = !this.isToolGroupExpanded(groupKey);

    this.toolGroupExpansionState = {
      ...this.toolGroupExpansionState,
      [groupKey]: nextExpanded
    };

    if (nextExpanded) {
      requestAnimationFrame(() => {
        headerElement?.scrollIntoView({
          block: 'start',
          inline: 'nearest',
          behavior: 'smooth'
        });
      });
    }

    this.cdr.markForCheck();
  }

  getToolDisplayName(tool: ToolOption): string {
    return TOOL_DISPLAY_NAME_OVERRIDES[tool.name]
      || this.formatFallbackToolDisplayName(
        tool.title?.trim()
        || tool.displayName?.trim()
        || tool.name
      );
  }

  getToolMeta(tool: ToolOption): string {
    const parts = [
      tool.readOnly ? 'Read only' : '',
      tool.destructive ? 'Destructive' : '',
      tool.requiresConfirmation ? 'Needs confirmation' : ''
    ]
      .filter((part): part is string => !!part);
    return parts.join(' · ');
  }

  trackToolGroup(index: number, group: ToolGroup): string {
    return group.key;
  }

  trackToolOption(index: number, tool: ToolOption): string {
    return tool.name;
  }

  get hasDefaultToolSelection(): boolean {
    return normalizeToolNameList(this.defaultToolNames).length > 0;
  }

  get isUsingDefaultToolSelection(): boolean {
    return areToolSelectionsEqual(this.selectedToolNames, this.defaultToolNames);
  }

  private buildGroupedAvailableTools(): ToolGroup[] {
    const groups = new Map<string, ToolGroup>();

    this.availableTools.forEach((tool, index) => {
      const group = this.resolveToolGroup(tool, index);
      const existingGroup = groups.get(group.key);

      if (existingGroup) {
        existingGroup.tools.push(tool);
        if (this.isToolSelected(tool.name)) {
          existingGroup.selectedCount += 1;
        }
        return;
      }

      groups.set(group.key, {
        ...group,
        tools: [tool],
        selectedCount: this.isToolSelected(tool.name) ? 1 : 0
      });
    });

    return [...groups.values()]
      .map(group => ({
        ...group,
        tools: [...group.tools].sort((left, right) => this.getToolDisplayName(left).localeCompare(this.getToolDisplayName(right)))
      }))
      .sort((left, right) => {
        if (left.order !== right.order) {
          return left.order - right.order;
        }

        return left.label.localeCompare(right.label);
      });
  }

  private resolveToolGroup(tool: ToolOption, index: number): Omit<ToolGroup, 'tools' | 'selectedCount'> {
    const key = (tool.groupKey || tool.category || 'other').trim() || 'other';
    const labelSource = tool.groupLabel || tool.category || key;
    const label = this.formatFallbackToolDisplayName(labelSource);
    const description = (tool.groupDescription || `Tools in the ${label.toLowerCase()} section.`).trim();
    const order = typeof tool.groupOrder === 'number' && Number.isFinite(tool.groupOrder)
      ? tool.groupOrder
      : 1000 + index;

    return {
      key,
      label,
      description,
      order
    };
  }

  private ensureToolGroupExpansionState(): void {
    const groupKeys = this.groupedAvailableTools.map(group => group.key);

    if (groupKeys.length === 0) {
      this.toolGroupExpansionState = {};
      return;
    }

    const nextState: Record<string, boolean> = {};
    for (const key of groupKeys) {
      nextState[key] = this.toolGroupExpansionState[key] ?? false;
    }

    const hasExpandedGroup = groupKeys.some(key => nextState[key]);
    if (!hasExpandedGroup) {
      const selectedGroupKeys = this.groupedAvailableTools
        .filter(group => group.selectedCount > 0)
        .map(group => group.key);
      const defaultExpandedKeys = selectedGroupKeys;

      for (const key of defaultExpandedKeys) {
        nextState[key] = true;
      }
    }

    this.toolGroupExpansionState = nextState;
  }

  private collapseToolGroups(): void {
    this.toolGroupExpansionState = Object.fromEntries(
      this.groupedAvailableTools.map(group => [group.key, false])
    );
  }

  private formatFallbackToolDisplayName(name: string): string {
    return String(name || '')
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(part => {
        const normalized = part.toLowerCase();
        if (normalized === 'uid') return 'UID';
        if (normalized === 'id') return 'ID';
        if (normalized === 'url') return 'URL';
        if (normalized === 'api') return 'API';
        if (normalized === 'ai') return 'AI';
        if (normalized === 'seo') return 'SEO';
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join(' ');
  }

  onAudioModelPreferenceChanged(modelKey: string): void {
    const normalizedModelKey = (modelKey || '').trim();
    this.currentAudioModelKey = normalizedModelKey;
    this.audioModelChanged.emit(normalizedModelKey);

    if (this.currentAudioVoice && !this.isVoiceAvailableForModel(this.currentAudioVoice, normalizedModelKey)) {
      this.currentAudioVoice = '';
      this.audioVoiceChanged.emit('');
    }
  }

  resetAudioPreferencesToDefault(): void {
    this.onAudioModelPreferenceChanged('');
    this.onAudioVoicePreferenceChanged('');
  }

  onImageModelPreferenceChanged(modelKey: string): void {
    const normalizedModelKey = (modelKey || '').trim();
    this.currentImageModelKey = normalizedModelKey;
    this.imageModelChanged.emit(normalizedModelKey);
  }

  resetImagePreferenceToDefault(): void {
    this.onImageModelPreferenceChanged('');
  }

  onVideoModelPreferenceChanged(modelKey: string): void {
    const normalizedModelKey = (modelKey || '').trim();
    this.currentVideoModelKey = normalizedModelKey;
    this.videoModelChanged.emit(normalizedModelKey);
  }

  resetVideoPreferenceToDefault(): void {
    this.onVideoModelPreferenceChanged('');
  }

  onAudioVoicePreferenceChanged(voice: string): void {
    const normalizedVoice = (voice || '').trim();
    this.currentAudioVoice = normalizedVoice;
    this.audioVoiceChanged.emit(normalizedVoice);
  }

  onAudioVoiceChipSelected(event: MouseEvent, modelKey: string, voice: string): void {
    event.stopPropagation();
    this.hideModelInfoTooltip(true);
    this.onAudioModelPreferenceChanged(modelKey);
    this.onAudioVoicePreferenceChanged(voice);
  }

  isAudioVoiceOptionSelected(modelKey: string, voice: string): boolean {
    return this.currentAudioModelKey === modelKey && (this.currentAudioVoice || '') === (voice || '');
  }

  isSystemDefaultMediaSelectionActive(tab: Exclude<ModelSettingsTab, 'default'>): boolean {
    switch (tab) {
      case 'audio':
        return !this.currentAudioModelKey;
      case 'image':
        return !this.currentImageModelKey;
      case 'video':
        return !this.currentVideoModelKey;
    }
  }

  selectModelPreferenceFromActiveTab(model: ModelOption): void {
    switch (this.activeModelSettingsTab) {
      case 'audio':
        this.onAudioModelPreferenceChanged(model.key);
        return;
      case 'image':
        this.onImageModelPreferenceChanged(model.key);
        return;
      case 'video':
        this.onVideoModelPreferenceChanged(model.key);
        return;
      default:
        this.selectModelFromPopover(model);
    }
  }

  onAddMenuAction(action: AddMenuAction): void {
    console.log('[chat-attachments] add menu action selected', {
      action,
      activeSelectorModal: this.activeSelectorModal
    });

    switch (action) {
      case 'image':
        this.openImageAttachmentPicker();
        break;
      case 'file':
        this.openFileAttachmentPicker();
        break;
      case 'context-business':
        this.showContextPopover = true;
        this.selectedContextIndex = 0;
        this.openContextPickerModal('business');
        break;
      case 'context-partner':
        this.showContextPopover = true;
        this.selectedContextIndex = 0;
        this.openContextPickerModal('partner');
        break;
      case 'context-funnel':
        this.showContextPopover = true;
        this.selectedContextIndex = 0;
        this.openContextPickerModal('funnel');
        break;
    }
  }

  onMoreMenuAction(action: MoreMenuAction): void {
    if (action === 'view' && !this.canOpenViewModal) {
      return;
    }

    switch (action) {
      case 'playbooks':
        this.openPlaybookPicker();
        break;
      case 'mode':
        this.openModePickerModal();
        break;
      case 'credits':
        this.closeSelectorModal();
        this.creditsPageRequested.emit();
        break;
      case 'history':
        this.closeSelectorModal();
        this.onSearchClick();
        break;
      case 'connectors':
        this.openConnectorsModal();
        break;
      case 'devices':
        this.openDevicesAuditModal();
        break;
      case 'skills':
        this.openSkillsModal();
        break;
      case 'tools':
        this.openToolPickerModal();
        break;
      case 'view':
        this.onViewClick();
        break;
      case 'assets':
        this.openThreadAssetsModal();
        break;
      case 'details':
        this.openThreadDetailsModal();
        break;
    }
  }

  activateSelectorModalOptionByNumber(position: number): boolean {
    if (!Number.isInteger(position) || position < 1) {
      return false;
    }

    if (this.activeSelectorModal === 'add') {
      const option = this.addMenuOptions[position - 1];
      if (!option) {
        return false;
      }

      this.onAddMenuAction(option.value);
      return true;
    }

    if (this.activeSelectorModal === 'more') {
      const option = this.moreMenuOptions[position - 1];
      if (!option || option.disabled) {
        return false;
      }

      this.onMoreMenuAction(option.value);
      return true;
    }

    return false;
  }

  onViewOptionSelectedFromModal(action: ChatThreadViewAction): void {
    this.closeSelectorModal();
    this.viewOptionSelected.emit(action);
  }

  onDisplayModeSelectedFromModal(displayMode: ChatDisplayMode): void {
    this.currentDisplayMode = displayMode;
    this.displayModePreferenceChanged.emit(displayMode);
  }

  onInlinePreviewModeSelectedFromModal(option: InlinePreviewModeOption): void {
    this.renderPreviewsInline = option.enabled;
    this.renderPreviewsInlinePreferenceChanged.emit(this.renderPreviewsInline);
  }

  onThreadBackendSelected(useLocalBackend: boolean): void {
    this.useLocalBackend = useLocalBackend;
    this.backendPreferenceChanged.emit(useLocalBackend);
  }

  onThreadRenameRequested(newName: string): void {
    this.renameThreadRequested.emit(newName);
  }

  onThreadNewConversationRequested(): void {
    this.closeSelectorModal();
    this.newConversationRequested.emit();
  }

  onThreadDeleteRequested(): void {
    this.closeSelectorModal();
    this.deleteThreadRequested.emit();
  }

  private openImageAttachmentPicker(): void {
    const input = this.imageAttachmentInput?.nativeElement;
    if (!input) {
      console.warn('[chat-attachments] image input ref missing');
      return;
    }

    console.log('[chat-attachments] triggering image picker');
    input.value = '';
    input.click();

    if (this.activeSelectorModal === 'add') {
      this.closeSelectorModal();
    }
  }

  private openFileAttachmentPicker(): void {
    const input = this.fileAttachmentInput?.nativeElement;
    if (!input) {
      console.warn('[chat-attachments] file input ref missing');
      return;
    }

    console.log('[chat-attachments] triggering file picker');
    input.value = '';
    input.click();

    if (this.activeSelectorModal === 'add') {
      this.closeSelectorModal();
    }
  }

  openDevicesAuditModal(target?: ChatDeviceCommandTarget | null): void {
    if (!this.devicesModalTemplate) {
      return;
    }

    this.deviceCommandModalTarget = target || null;
    this.closeSelectorModal();
    this.uiModalService.open({
      title: 'Devices',
      subtitle: 'Register computers or request browser access, then inspect recent command activity.',
      bodyTemplate: this.devicesModalTemplate,
      bodyClass: 'chat-devices-modal-body',
      size: 'full',
      hideFooter: true,
      onClose: () => {
        this.deviceCommandModalTarget = null;
      }
    });
  }

  private openConnectorsModal(): void {
    if (!this.connectorsModalTemplate) {
      return;
    }

    this.closeSelectorModal();
    this.uiModalService.open({
      title: 'Connectors',
      subtitle: 'Configure custom MCP servers that your agent can discover and access.',
      bodyTemplate: this.connectorsModalTemplate,
      bodyClass: 'chat-connectors-modal-body',
      size: 'full',
      hideFooter: true
    });
  }

  private openSkillsModal(): void {
    if (!this.skillsModalTemplate) {
      return;
    }

    this.selectedSkillIndex = this.availableSkills.length ? Math.min(this.selectedSkillIndex, this.availableSkills.length - 1) : 0;
    this.closeSelectorModal();
    this.uiModalService.open({
      title: 'Skills',
      subtitle: 'Insert a slash skill trigger for this agency.',
      bodyTemplate: this.skillsModalTemplate,
      bodyClass: 'chat-skills-modal-body',
      size: 'lg',
      hideFooter: true
    });

    window.setTimeout(() => {
      const modal = document.querySelector('.skills-modal') as HTMLElement | null;
      modal?.focus({ preventScroll: true });
    }, 0);
  }

  onSkillSelected(skill: AgencySkillSummary): void {
    const trigger = `/${skill.name}`;
    const current = this.newMessage || '';
    const selection = this.getTextareaSelection();
    const nextValue = this.insertSkillTriggerAtSelection(current, trigger, selection.start, selection.end);

    this.uiModalService.close();
    if (this.chatTextarea?.nativeElement) {
      this.chatTextarea.nativeElement.value = nextValue;
    }
    this.messageChanged.emit({
      value: nextValue,
      event: new Event('input')
    });
    this.focusInputAt(nextValue.length);
  }

  onSkillsModalKeyDown(event: KeyboardEvent): void {
    if (!this.availableSkills.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedSkillIndex = (this.selectedSkillIndex + 1) % this.availableSkills.length;
      this.scrollSelectedSkillIntoView();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedSkillIndex = (this.selectedSkillIndex - 1 + this.availableSkills.length) % this.availableSkills.length;
      this.scrollSelectedSkillIntoView();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const skill = this.availableSkills[this.selectedSkillIndex];
      if (skill) {
        this.onSkillSelected(skill);
      }
    }
  }

  private scrollSelectedSkillIntoView(): void {
    window.setTimeout(() => {
      const selected = document.querySelector('.skills-modal-item.is-selected') as HTMLElement | null;
      selected?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  private loadAgencySkills(): void {
    this.skillsSubscription?.unsubscribe();
    this.availableSkills = [];

    const resolvedAgencyId = this.agencyId || this.authService.aid || '';
    if (!resolvedAgencyId) {
      return;
    }

    this.skillsSubscription = this.afs
      .collection(`users/${resolvedAgencyId}/skills`, ref => ref.orderBy('name', 'asc'))
      .valueChanges({ idField: 'id' })
      .subscribe((skills: any[]) => {
        this.availableSkills = skills as AgencySkillSummary[];
        if (this.selectedSkillIndex >= this.availableSkills.length) {
          this.selectedSkillIndex = 0;
        }
        this.cdr.markForCheck();
      });
  }

  private shouldOpenSkillsModalForSlash(event: KeyboardEvent): boolean {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) {
      return false;
    }

    const textarea = this.chatTextarea?.nativeElement;
    if (!textarea || textarea.disabled) {
      return false;
    }

    const cursor = textarea.selectionStart ?? 0;
    const value = textarea.value || '';
    const previousCharacter = cursor > 0 ? value.charAt(cursor - 1) : '';
    const nextCharacter = value.charAt(cursor) || '';

    return (!previousCharacter || /\s/.test(previousCharacter)) && (!nextCharacter || /\s/.test(nextCharacter));
  }

  private queueSlashSkillsModal(): void {
    if (this.slashSkillModalQueued) {
      return;
    }

    this.slashSkillModalQueued = true;
    window.setTimeout(() => {
      this.slashSkillModalQueued = false;
      this.openSkillsModal();
    }, 0);
  }

  private getTextareaSelection(): { start: number; end: number } {
    const textarea = this.chatTextarea?.nativeElement;
    const fallback = (this.newMessage || '').length;

    return {
      start: textarea?.selectionStart ?? fallback,
      end: textarea?.selectionEnd ?? fallback
    };
  }

  private insertSkillTriggerAtSelection(current: string, trigger: string, selectionStart: number, selectionEnd: number): string {
    const slashStart = this.findSlashTokenStart(current, selectionStart);
    const start = slashStart >= 0 ? slashStart : selectionStart;
    const before = current.slice(0, start).replace(/[ \t]+$/, '');
    const after = current.slice(selectionEnd).replace(/^[ \t]+/, '');
    const prefix = before ? `${before} ` : '';
    const suffix = after ? ` ${after}` : '';

    return `${prefix}${trigger} ${suffix}`;
  }

  private findSlashTokenStart(value: string, cursor: number): number {
    let index = Math.max(0, cursor - 1);

    while (index >= 0 && !/\s/.test(value.charAt(index))) {
      if (value.charAt(index) === '/') {
        const previousCharacter = index > 0 ? value.charAt(index - 1) : '';
        return !previousCharacter || /\s/.test(previousCharacter) ? index : -1;
      }
      index -= 1;
    }

    return -1;
  }

  private handleSkillTokenDeletion(event: KeyboardEvent): boolean {
    if (event.key !== 'Backspace' && event.key !== 'Delete') {
      return false;
    }

    const textarea = this.chatTextarea?.nativeElement;
    if (!textarea || textarea.disabled) {
      return false;
    }

    const value = textarea.value || '';
    const selectionStart = textarea.selectionStart ?? 0;
    const selectionEnd = textarea.selectionEnd ?? selectionStart;
    const token = this.findSkillTokenForDeletion(value, selectionStart, selectionEnd, event.key);

    if (!token) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();

    const deleteStart = token.start;
    let deleteEnd = token.end;
    if (value.charAt(deleteEnd) === ' ') {
      deleteEnd += 1;
    } else if (deleteStart > 0 && value.charAt(deleteStart - 1) === ' ') {
      deleteEnd = token.end;
    }

    const nextValue = `${value.slice(0, deleteStart)}${value.slice(deleteEnd)}`;
    textarea.value = nextValue;
    this.messageChanged.emit({
      value: nextValue,
      event: new Event('input')
    });
    this.focusInputAt(deleteStart);

    return true;
  }

  private findSkillTokenForDeletion(value: string, selectionStart: number, selectionEnd: number, key: string): ComposerSkillTokenRange | null {
    const ranges = this.getComposerSkillTokenRanges(value);
    if (!ranges.length) {
      return null;
    }

    if (selectionStart !== selectionEnd) {
      return ranges.find(range => selectionStart < range.end && selectionEnd > range.start) || null;
    }

    return ranges.find(range => {
      if (key === 'Backspace') {
        return selectionStart > range.start && selectionStart <= range.end;
      }

      return selectionStart >= range.start && selectionStart < range.end;
    }) || null;
  }

  private getComposerSkillTokenRanges(value: string): ComposerSkillTokenRange[] {
    const skillNames = new Set(this.availableSkills.map(skill => skill.name));
    if (!value || !skillNames.size) {
      return [];
    }

    const ranges: ComposerSkillTokenRange[] = [];
    const triggerPattern = /(^|\s)\/([a-z0-9_-]+)(?=\s|$)/gi;
    let match: RegExpExecArray | null;

    while ((match = triggerPattern.exec(value)) !== null) {
      const leading = match[1] || '';
      const name = match[2] || '';
      if (!skillNames.has(name)) {
        continue;
      }

      const start = match.index + leading.length;
      ranges.push({
        name,
        start,
        end: triggerPattern.lastIndex
      });
    }

    return ranges;
  }

  private buildComposerSkillParts(value: string): ComposerSkillTokenPart[] {
    if (!value) {
      return [];
    }

    const skillNames = new Set(this.availableSkills.map(skill => skill.name));
    if (!skillNames.size) {
      return [{ type: 'text', value }];
    }

    const parts: ComposerSkillTokenPart[] = [];
    const triggerPattern = /(^|\s)\/([a-z0-9_-]+)(?=\s|$)/gi;
    let cursor = 0;
    let match: RegExpExecArray | null;

    while ((match = triggerPattern.exec(value)) !== null) {
      const leading = match[1] || '';
      const name = match[2] || '';
      const tokenStart = match.index + leading.length;

      if (tokenStart > cursor) {
        parts.push({ type: 'text', value: value.slice(cursor, tokenStart) });
      }

      if (skillNames.has(name)) {
        parts.push({ type: 'skill', value: name });
      } else {
        parts.push({ type: 'text', value: value.slice(tokenStart, triggerPattern.lastIndex) });
      }

      cursor = triggerPattern.lastIndex;
    }

    if (cursor < value.length) {
      parts.push({ type: 'text', value: value.slice(cursor) });
    }

    return parts;
  }

  private getFilesFromInputEvent(event: Event): File[] {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);

    if (files.length === 0) {
      console.log('[chat-attachments] picker closed without selection');
    }

    if (input) {
      input.value = '';
    }

    return files;
  }

  private openSelectorModal(kind: SelectorModalKind): void {
    const config = this.getSelectorModalConfig(kind);

    if (!config.bodyTemplate) {
      return;
    }

    this.activeSelectorModal = kind;
    this.uiModalService.open({
      ...config,
      hideFooter: config.hideFooter ?? true,
      onClose: () => this.handleSelectorModalClosed()
    });

    if (kind === 'assets') {
      this.focusedThreadAssetIndex = -1;
      this.focusThreadAssetSearchFieldLater();
    }
  }

  private getSelectorModalConfig(kind: SelectorModalKind): {
    title?: string;
    subtitle?: string;
    bodyTemplate?: TemplateRef<any>;
    headerLeftTemplate?: TemplateRef<any>;
    headerRightTemplate?: TemplateRef<any>;
    footerRightTemplate?: TemplateRef<any>;
    hideFooter?: boolean;
    size: 'sm' | 'md' | 'lg';
  } {
    switch (kind) {
      case 'add':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.addMenuPopoverTemplate,
          size: 'sm'
        };
      case 'more':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.moreMenuPopoverTemplate,
          size: 'sm'
        };
      case 'agent':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.agentPopoverTemplate,
          size: 'md'
        };
      case 'mode':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.modePopoverTemplate,
          size: 'md'
        };
      case 'model':
        return {
          headerLeftTemplate: this.modelHeaderTemplate,
          headerRightTemplate: this.modelHeaderActionsTemplate,
          bodyTemplate: this.modelPopoverTemplate,
          size: 'lg'
        };
      case 'tools':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.toolPopoverTemplate,
          size: 'lg'
        };
      case 'prompts':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.promptPopoverTemplate,
          size: 'lg'
        };
      case 'context':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.contextPopoverTemplate,
          footerRightTemplate: this.contextFooterTemplate,
          hideFooter: false,
          size: 'lg'
        };
      case 'view':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.viewPopoverTemplate,
          size: 'sm'
        };
      case 'details':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.threadDetailsPopoverTemplate,
          size: 'lg'
        };
      case 'contextUsage':
        return {
          headerLeftTemplate: this.selectorModalHeaderTemplate,
          bodyTemplate: this.contextUsagePopoverTemplate,
          size: 'sm'
        };
      case 'assets':
        return {
          headerLeftTemplate: this.threadAssetsHeaderTemplate,
          bodyTemplate: this.threadAssetsTemplate,
          size: 'lg',
          hideFooter: true
        };
    }
  }

  private handleSelectorModalClosed(): void {
    const pendingThreadAssetSelection = this.pendingThreadAssetSelection;
    this.pendingThreadAssetSelection = null;
    this.hideModelInfoTooltip(true);
    this.optimisticModelCatalogLoading = false;
    this.optimisticToolCatalogLoading = false;
    this.activeSelectorModal = null;
    this.resetSelectorState();
    this.selectorModalClosed.emit();
    this.focusInput();

    if (pendingThreadAssetSelection) {
      this.threadAssetSelected.emit(pendingThreadAssetSelection);
    }
  }

  private resetSelectorState(): void {
    this.hideModelInfoTooltip(true);
    this.showPromptPopover = false;
    this.showContextPopover = false;
    this.showThreadDetailsPopover = false;
    this.contextPickerInitialSource = 'business';
    this.selectedThreadAssetFilter = 'all';
    this.threadAssetSearchQuery = '';
    this.focusedThreadAssetIndex = -1;
  }

  private getThreadAssetFilterKinds(filter: ThreadAssetFilter): ChatThreadAssetKind[] {
    return THREAD_ASSET_FILTER_DEFINITIONS.find(option => option.value === filter)?.kinds || [];
  }

  private syncThreadAssetFilterSelection(): void {
    if (this.selectedThreadAssetFilter === 'all') {
      this.syncThreadAssetFocusedIndex();
      return;
    }

    const isStillAvailable = this.threadAssetFilterOptions.some(option => option.value === this.selectedThreadAssetFilter);
    if (!isStillAvailable) {
      this.selectedThreadAssetFilter = 'all';
      this.cdr.markForCheck();
    }

    this.syncThreadAssetFocusedIndex();
  }

  private focusThreadAssetByIndex(index: number): void {
    this.focusedThreadAssetIndex = index;
    this.cdr.markForCheck();

    setTimeout(() => {
      const button = this.threadAssetItemButtons?.get(index)?.nativeElement;
      if (!button) {
        return;
      }

      button.focus({ preventScroll: true });
      button.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    });
  }

  private focusThreadAssetSearchField(): void {
    this.focusedThreadAssetIndex = -1;
    this.threadAssetSearchInput?.nativeElement.focus({ preventScroll: true });
  }

  private focusThreadAssetSearchFieldLater(): void {
    setTimeout(() => this.focusThreadAssetSearchField());
  }

  private syncThreadAssetFocusedIndex(): void {
    if (this.focusedThreadAssetIndex < 0) {
      return;
    }

    const itemCount = this.filteredThreadAssetItems.length;
    if (!itemCount) {
      this.focusedThreadAssetIndex = -1;
      return;
    }

    if (this.focusedThreadAssetIndex >= itemCount) {
      this.focusedThreadAssetIndex = itemCount - 1;
    }
  }

  private calculateThreadAssetSearchScore(asset: ChatThreadAssetItem, searchTerm: string): number {
    if (!asset || !searchTerm) {
      return 0;
    }

    if (asset.title && this.isPerfectThreadAssetMatch(searchTerm, asset.title)) {
      return 1000;
    }

    if (asset.typeLabel && this.isPerfectThreadAssetMatch(searchTerm, asset.typeLabel)) {
      return 900;
    }

    const kindLabel = this.getThreadAssetKindSearchLabel(asset.kind);
    const searchFields = [
      { field: asset.title || '', weight: 3 },
      { field: asset.typeLabel || '', weight: 2.5 },
      { field: kindLabel, weight: 2 },
      { field: asset.subtitle || '', weight: 1.5 },
      { field: `${asset.title || ''} ${asset.typeLabel || ''} ${kindLabel} ${asset.subtitle || ''}`.trim(), weight: 1.25 }
    ];

    let bestScore = 0;
    for (const { field, weight } of searchFields) {
      if (field && typeof field === 'string' && field.trim()) {
        const score = this.fuzzyThreadAssetMatch(searchTerm, field) * weight;
        bestScore = Math.max(bestScore, score);
      }
    }

    return bestScore;
  }

  private isPerfectThreadAssetMatch(searchTerm: string, fieldValue: string): boolean {
    if (!searchTerm || !fieldValue) {
      return false;
    }

    const search = searchTerm.toLowerCase().trim();
    const field = fieldValue.toLowerCase().trim();
    return field === search || field.includes(search);
  }

  private fuzzyThreadAssetMatch(searchTerm: string, text: string): number {
    if (!searchTerm || !text) {
      return 0;
    }

    const search = String(searchTerm).toLowerCase().trim();
    const target = String(text).toLowerCase().trim();
    if (!search || !target) {
      return 0;
    }

    if (target === search) {
      return 100;
    }

    if (target.startsWith(search)) {
      return 90;
    }

    if (target.includes(search)) {
      return 70;
    }

    const searchWords = search.split(/\s+/).filter(word => word.length > 0);
    const targetWords = target.split(/\s+/).filter(word => word.length > 0);

    const prefixMatches = searchWords.filter(searchWord => targetWords.some(targetWord => targetWord.startsWith(searchWord))).length;
    if (prefixMatches === searchWords.length && searchWords.length > 0) {
      return 62 * (prefixMatches / searchWords.length);
    }

    let wordsFound = 0;
    for (const searchWord of searchWords) {
      for (const targetWord of targetWords) {
        if (targetWord.includes(searchWord) || searchWord.includes(targetWord)) {
          wordsFound++;
          break;
        }
      }
    }

    if (wordsFound === searchWords.length && searchWords.length > 0) {
      return 60 * (wordsFound / searchWords.length);
    }

    if (search.length >= 6) {
      return 0;
    }

    let score = 0;
    let searchIndex = 0;
    for (let index = 0; index < target.length && searchIndex < search.length; index++) {
      if (target[index] === search[searchIndex]) {
        score += 10;
        searchIndex++;
      }
    }

    if (searchIndex === search.length) {
      score += 20;
    }

    const lengthRatio = Math.min(search.length / target.length, 1);
    return score * lengthRatio;
  }

  private getThreadAssetKindSearchLabel(kind: ChatThreadAssetKind): string {
    switch (kind) {
      case 'tool-preview':
        return 'preview';
      case 'generated-image':
        return 'image';
      case 'generated-audio':
        return 'audio';
      case 'artifact':
        return 'file';
      default:
        return kind;
    }
  }

  private hideModelInfoTooltip(immediate: boolean = false): void {
    this.activeModelInfoTooltipElement = null;
    this.activeModelInfoTooltipText = null;

    if (immediate) {
      this.tooltipService.hideImmediate();
      return;
    }

    this.tooltipService.hide();
  }

  private isVoiceAvailableForModel(voice: string, selectedModelKey: string): boolean {
    if (!voice) {
      return true;
    }

    const effectiveModelKey = selectedModelKey || this.defaultAudioModelKey;
    return this.getAudioVoiceOptionsForModel(effectiveModelKey).some(option => option.value === voice);
  }

  getAudioVoiceOptionsForModel(modelKey: string): Array<{ value: string; label: string }> {
    const systemDefaultVoice = this.formatAudioVoiceLabel(this.defaultAudioVoice || 'alloy');
    const fallbackOption = [{ value: '', label: `System default (${systemDefaultVoice})` }];
    const effectiveModelKey = modelKey || this.defaultAudioModelKey;
    const configuredOptions = effectiveModelKey ? (this.audioVoiceOptionsByModel[effectiveModelKey] || []) : [];

    if (configuredOptions.length > 1) {
      return configuredOptions.map(option => option.value === ''
        ? { ...option, label: `System default (${systemDefaultVoice})` }
        : option);
    }

    const inferredOptions = this.getFallbackAudioVoiceOptionsForModel(effectiveModelKey, systemDefaultVoice);
    if (inferredOptions.length > 1) {
      return inferredOptions;
    }

    if (!configuredOptions.length) {
      return fallbackOption;
    }

    return configuredOptions.map(option => option.value === ''
      ? { ...option, label: `System default (${systemDefaultVoice})` }
      : option);
  }

  private formatAudioVoiceLabel(voice: string): string {
    const normalizedVoice = voice.trim();
    if (!normalizedVoice) {
      return 'System';
    }

    return normalizedVoice.charAt(0).toUpperCase() + normalizedVoice.slice(1);
  }

  private getFallbackAudioVoiceOptionsForModel(modelKey: string, systemDefaultVoice: string): Array<{ value: string; label: string }> {
    if (!this.supportsOpenAIAudioVoices(modelKey)) {
      return [{ value: '', label: `System default (${systemDefaultVoice})` }];
    }

    return [
      { value: '', label: `System default (${systemDefaultVoice})` },
      ...OPENAI_AUDIO_VOICE_OPTIONS
    ];
  }

  private supportsOpenAIAudioVoices(modelKey: string): boolean {
    const normalizedModelKey = String(modelKey || '').trim().toLowerCase();
    if (!normalizedModelKey) {
      return false;
    }

    if (normalizedModelKey.startsWith('openai/')) {
      return normalizedModelKey.includes('audio')
        || normalizedModelKey.includes('realtime')
        || normalizedModelKey.includes('tts');
    }

    return normalizedModelKey.includes('gpt-4o-audio')
      || normalizedModelKey.includes('audio-preview')
      || normalizedModelKey.includes('realtime-preview')
      || normalizedModelKey.includes('mini-tts');
  }

  private getModelDisplayName(modelKey: string, catalog: ModelOption[], fallbackLabel: string): string {
    const normalizedModelKey = (modelKey || '').trim();
    if (!normalizedModelKey) {
      return fallbackLabel;
    }

    return catalog.find(model => model.key === normalizedModelKey)?.displayName || normalizedModelKey;
  }

  onPlaybookSelectedFromPopover(playbook: PlaybookEntry): void {
    this.closeSelectorModal();
    this.activatePlaybook(playbook, 'internal');
    this.cdr.markForCheck();
  }

  onContextItemSelectedFromPopover(event: any): void {
    const item = event.item || event;
    const keepOpen = event.keepOpen || false;
    const parentId = item.parentId || undefined;
    const parentType = item.parentType || undefined;
    const parentDisplayName = item.parentDisplayName || undefined;

    switch (item.type) {
      case 'business':
        this.attachedContextService.addBusiness(item.id, item.displayName);
        break;
      case 'partner':
        this.attachedContextService.addPartner(item.id, item.displayName);
        break;
      case 'funnel':
        this.attachedContextService.addFunnel(item.id, item.displayName);
        break;
      case 'page':
        this.attachedContextService.addPage(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'form':
        this.attachedContextService.addForm(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'note':
        this.attachedContextService.addNote(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'plan':
        this.attachedContextService.addPlan(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'proposal':
        this.attachedContextService.addProposal(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'task':
        this.attachedContextService.addTask(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'ticket':
        this.attachedContextService.addTicket(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'app':
        this.attachedContextService.addApp(item.id, item.displayName, parentId, parentType, parentDisplayName);
        break;
      case 'media':
        this.attachedContextService.addMedia(item.id, item.displayName, item.url, item.mediaType, parentId, parentType, parentDisplayName);
        break;
    }

    if (parentId && parentType && parentDisplayName) {
      switch (parentType) {
        case 'business':
          this.attachedContextService.addBusiness(parentId, parentDisplayName);
          break;
        case 'funnel':
          this.attachedContextService.addFunnel(parentId, parentDisplayName);
          break;
      }
    }

    if (!keepOpen) {
      this.closeSelectorModal();
      this.focusInput();
    }

    this.contextItemSelected.emit(event);
    this.cdr.markForCheck();
  }

  onBusinessSelectedFromPopover(event: { uid: string; name: string }): void {
    this.contextUid = event.uid;
    this.contextBusinessName = event.name;
    this.loadContextItems(event.uid);
    this.businessSelectedForContext.emit(event);
  }

  onBusinessAttachedFromPopover(event: { uid: string; name: string }): void {
    this.closeSelectorModal();
    this.focusInput();
    this.businessAttachedForContext.emit(event);
    this.cdr.markForCheck();
  }

  // ── Data loading ───────────────────────────────────────────

  private loadPlaybooks(): void {
    if (!this.agent) {
      this.availablePlaybooks = [];
      this.contextPrompts = [];
      return;
    }
    this.availablePlaybooks = getPlaybooksForAgent(this.agent);
    this.contextPrompts = this.availablePlaybooks;
    this.cdr.markForCheck();
  }

  private hydratePlaybookPrefillFromInputs(): void {
    const slug = (this.playbookPrefillSlug || '').trim();

    if (!slug) {
      this.lastExternalPlaybookSignature = null;

      if (this.activePlaybookSource === 'external') {
        this.resetActivePlaybook();
        this.cdr.markForCheck();
      }

      return;
    }

    const playbook = getPlaybookBySlug(slug);
    if (!playbook) {
      return;
    }

    const normalizedFieldValues = playbook.fields.reduce<Record<string, string>>((acc, field) => {
      acc[field.id] = String(this.playbookPrefillFieldValues?.[field.id] || '');
      return acc;
    }, {});

    const signature = `${slug}::${JSON.stringify(normalizedFieldValues)}`;
    if (this.lastExternalPlaybookSignature === signature && this.activePlaybookSource === 'external') {
      return;
    }

    this.lastExternalPlaybookSignature = signature;
    this.activatePlaybook(playbook, 'external', normalizedFieldValues);
    this.cdr.markForCheck();
  }

  private activatePlaybook(
    playbook: PlaybookEntry,
    source: 'internal' | 'external',
    fieldValues: Record<string, string> = {}
  ): void {
    this.contextFieldItemSubs.forEach(subscription => subscription.unsubscribe());
    this.contextFieldItemSubs = [];
    this.activePlaybook = playbook;
    this.activePlaybookSource = source;
    this.playbookFieldValues = playbook.fields.reduce<Record<string, string>>((acc, field) => {
      acc[field.id] = String(fieldValues[field.id] || '');
      return acc;
    }, {});
    this.playbookContextEntities = {};
    this.showSystemPrompt = false;
    this.fieldSelectField = null;
    this.fieldSelectCustomValue = '';
    this.contextFieldActive = null;
    this.contextFieldItems = [];
    this.contextFieldNewName = '';
    this.playbookActivated.emit(playbook);
  }

  private resetActivePlaybook(): void {
    this.activePlaybook = null;
    this.activePlaybookSource = null;
    this.playbookFieldValues = {};
    this.playbookContextEntities = {};
    this.showSystemPrompt = false;
    this.fieldSelectField = null;
    this.fieldSelectCustomValue = '';
    this.contextFieldActive = null;
    this.contextFieldItems = [];
    this.contextFieldNewName = '';
    this.contextFieldItemSubs.forEach(subscription => subscription.unsubscribe());
    this.contextFieldItemSubs = [];
  }

  // ── Inline playbook methods ────────────────────────────────

  getFieldDef(fieldId: string): PlaybookField | undefined {
    return this.activePlaybook?.fields.find(f => f.id === fieldId);
  }

  onPlaybookFieldInput(fieldId: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.playbookFieldValues = { ...this.playbookFieldValues, [fieldId]: value };
  }

  onPlaybookEnter(event: Event): void {
    if (this.canSubmitPlaybook) {
      event.preventDefault();
      this.submitPlaybook();
    }
  }

  get canSubmitPlaybook(): boolean {
    if (!this.activePlaybook) return false;
    return this.activePlaybook.fields
      .filter(f => f.required !== false)
      .every(f => {
        if (f.type === 'context' && f.defaultValue) return true; // context fields auto-fill
        return (this.playbookFieldValues[f.id] || '').trim().length > 0;
      });
  }

  submitPlaybook(): void {
    if (!this.activePlaybook || !this.canSubmitPlaybook) return;
    // Fill in defaultValue for any unfilled context fields
    const finalValues = { ...this.playbookFieldValues };
    for (const field of this.activePlaybook.fields) {
      if (field.type === 'context' && !finalValues[field.id]?.trim()) {
        finalValues[field.id] = field.defaultValue || '';
      }
    }
    const resolved = resolvePlaybookPrompt(this.activePlaybook, finalValues);
    const effectivePlaybook = this.effectiveSystemPrompt !== this.activePlaybook.systemPromptOverride
      ? { ...this.activePlaybook, systemPromptOverride: this.effectiveSystemPrompt }
      : this.activePlaybook;
    this.playbookSubmitted.emit({
      playbook: effectivePlaybook,
      resolvedPrompt: resolved,
      fieldValues: finalValues,
    });
    this.resetActivePlaybook();
    this.cdr.markForCheck();
  }

  dismissPlaybook(): void {
    this.resetActivePlaybook();
    this.cdr.markForCheck();
    this.focusInput();
  }

  toggleSystemPrompt(): void {
    this.showSystemPrompt = !this.showSystemPrompt;
    this.cdr.markForCheck();
  }

  get hasSystemPromptEdits(): boolean {
    if (!this.activePlaybook?.systemPromptOverride) return false;
    return this.editableSystemPrompt !== this.activePlaybook.systemPromptOverride;
  }

  get effectiveSystemPrompt(): string | null {
    if (!this.activePlaybook) return null;
    return this.systemPromptOverrides[this.activePlaybook.slug] ?? this.activePlaybook.systemPromptOverride;
  }

  openSystemPromptModal(): void {
    if (!this.activePlaybook?.systemPromptOverride || !this.systemPromptTemplate) return;
    this.editableSystemPrompt = this.systemPromptOverrides[this.activePlaybook.slug] ?? this.activePlaybook.systemPromptOverride;
    this.cdr.markForCheck();
    this.uiModalService.open({
      title: 'System prompt',
      bodyTemplate: this.systemPromptTemplate,
      hideFooter: true,
      size: 'md',
      onClose: () => {
        this.cdr.markForCheck();
      }
    });
  }

  resetSystemPrompt(): void {
    if (!this.activePlaybook?.systemPromptOverride) return;
    this.editableSystemPrompt = this.activePlaybook.systemPromptOverride;
    delete this.systemPromptOverrides[this.activePlaybook.slug];
    this.cdr.markForCheck();
  }

  saveSystemPrompt(): void {
    if (!this.activePlaybook) return;
    const trimmed = this.editableSystemPrompt.trim();
    if (trimmed && trimmed !== this.activePlaybook.systemPromptOverride) {
      this.systemPromptOverrides[this.activePlaybook.slug] = trimmed;
    } else {
      delete this.systemPromptOverrides[this.activePlaybook.slug];
    }
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  getSelectedOptionIcon(fieldId: string): string | null {
    const field = this.getFieldDef(fieldId);
    if (!field?.options || field.type === 'multi-select') return null;
    const val = this.playbookFieldValues[fieldId];
    if (!val) return null;
    const opt = field.options.find(o => o.value === val);
    return opt?.icon || null;
  }

  getSelectedOptionLabel(fieldId: string): string | null {
    const field = this.getFieldDef(fieldId);
    if (!field?.options) return null;
    const val = this.playbookFieldValues[fieldId];
    if (!val) return null;
    if (field.type === 'multi-select') {
      const values = val.split(', ').filter(v => v);
      if (!values.length) return null;
      const labels = values.map(v => field.options!.find(o => o.value === v)?.label || v);
      return labels.join(', ');
    }
    return field.options.find(o => o.value === val)?.label || val;
  }

  openFieldSelectModal(field: PlaybookField): void {
    this.fieldSelectField = field;
    this.fieldSelectCustomValue = '';
    this.cdr.markForCheck();
    if (!this.fieldSelectTemplate) return;
    const title = field.label.replace(/\b\w/g, c => c.toUpperCase());
    this.uiModalService.open({
      title,
      bodyTemplate: this.fieldSelectTemplate,
      hideFooter: true,
      size: 'sm',
      onClose: () => {
        this.fieldSelectField = null;
        this.fieldSelectCustomValue = '';
        this.cdr.markForCheck();
      }
    });
  }

  selectFieldOption(fieldId: string, value: string): void {
    const field = this.getFieldDef(fieldId);
    if (field?.type === 'multi-select') {
      const current = this.playbookFieldValues[fieldId];
      const values = current ? current.split(', ').filter(v => v) : [];
      const idx = values.indexOf(value);
      if (idx >= 0) {
        values.splice(idx, 1);
      } else {
        values.push(value);
      }
      this.playbookFieldValues = { ...this.playbookFieldValues, [fieldId]: values.join(', ') };
      this.cdr.markForCheck();
      return; // don't close modal for multi-select
    }
    this.playbookFieldValues = { ...this.playbookFieldValues, [fieldId]: value };
    this.fieldSelectField = null;
    this.fieldSelectCustomValue = '';
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  isMultiSelectOptionSelected(fieldId: string, value: string): boolean {
    const current = this.playbookFieldValues[fieldId];
    if (!current) return false;
    return current.split(', ').includes(value);
  }

  confirmMultiSelect(): void {
    this.fieldSelectField = null;
    this.fieldSelectCustomValue = '';
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  applyCustomValue(fieldId: string): void {
    const val = this.fieldSelectCustomValue.trim();
    if (!val) return;
    const field = this.getFieldDef(fieldId);
    if (field?.type === 'multi-select') {
      const current = this.playbookFieldValues[fieldId];
      const values = current ? current.split(', ').filter(v => v) : [];
      // Add custom values (comma separated)
      val.split(',').map(v => v.trim()).filter(v => v).forEach(v => {
        if (!values.includes(v)) values.push(v);
      });
      this.playbookFieldValues = { ...this.playbookFieldValues, [fieldId]: values.join(', ') };
    } else {
      this.playbookFieldValues = { ...this.playbookFieldValues, [fieldId]: val };
    }
    this.fieldSelectCustomValue = '';
    this.fieldSelectField = null;
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  getInputSize(field: PlaybookField): number {
    const val = this.playbookFieldValues[field.id];
    if (val && val.length > 0) return Math.max(val.length + 1, 4);
    const placeholder = field.placeholder || field.label;
    return Math.max(placeholder.length + 1, 4);
  }

  // ── Context field methods (for playbook context-type fields) ──

  openContextFieldModal(field: PlaybookField): void {
    this.contextFieldActive = field;
    this.contextFieldItems = [];
    this.contextFieldBusinessUid = '';
    this.contextFieldSegment = 'existing';
    this.contextFieldNewName = '';

    // If this is a proposal field with dependsOn business, pre-filter to that business
    if (field.contextType === 'proposal' && field.dependsOn) {
      const parentEntity = this.playbookContextEntities[field.dependsOn];
      if (parentEntity && parentEntity.id) {
        this.contextFieldBusinessUid = parentEntity.id;
        this.loadContextFieldItems(parentEntity.id, 'proposal');
      }
    }

    if (!this.contextFieldTemplate) return;
    const title = field.contextType === 'business' ? 'Select business' : 'Select ' + (field.contextType || field.label);
    this.uiModalService.open({
      title,
      bodyTemplate: this.contextFieldTemplate,
      hideFooter: true,
      size: 'md',
      onClose: () => {
        this.contextFieldActive = null;
        this.contextFieldItems = [];
        this.contextFieldItemSubs.forEach(s => s.unsubscribe());
        this.contextFieldItemSubs = [];
        this.cdr.markForCheck();
      }
    });
  }

  selectContextFieldNew(field: PlaybookField): void {
    const name = this.contextFieldNewName.trim();
    const base = field.defaultValue || 'a new ' + (field.contextType || 'item');
    const value = name ? `${base} called ${name}` : base;
    this.playbookFieldValues = { ...this.playbookFieldValues, [field.id]: value };
    // Remove any stored entity reference
    const { [field.id]: _, ...rest } = this.playbookContextEntities;
    this.playbookContextEntities = rest;
    // If this is a business selection, reset dependent fields
    this.resetDependentContextFields(field.id);
    this.contextFieldActive = null;
    this.contextFieldNewName = '';
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  closeContextFieldModal(): void {
    this.contextFieldActive = null;
    this.contextFieldItems = [];
    this.contextFieldNewName = '';
    this.contextFieldItemSubs.forEach(s => s.unsubscribe());
    this.contextFieldItemSubs = [];
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  selectContextFieldBusiness(field: PlaybookField, biz: any): void {
    const uid = biz.id || biz.uid;
    const name = biz.businessName || biz.name || 'Unknown';
    this.playbookFieldValues = { ...this.playbookFieldValues, [field.id]: name };
    this.playbookContextEntities = {
      ...this.playbookContextEntities,
      [field.id]: { id: uid, name, type: 'business' }
    };
    // Reset dependent fields
    this.resetDependentContextFields(field.id);
    this.contextFieldActive = null;
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  selectContextFieldItem(field: PlaybookField, item: any): void {
    const parentEntity = field.dependsOn ? this.playbookContextEntities[field.dependsOn] : undefined;
    const parentId = parentEntity?.id;
    const parentType = parentId ? 'business' : undefined;

    this.playbookFieldValues = { ...this.playbookFieldValues, [field.id]: item.displayName || item.name || '' };
    this.playbookContextEntities = {
      ...this.playbookContextEntities,
      [field.id]: { id: item.id, name: item.displayName || item.name, type: field.contextType || '' }
    };
    this.contextFieldActive = null;
    this.uiModalService.close();
    this.cdr.markForCheck();
  }

  private resetDependentContextFields(fieldId: string): void {
    if (!this.activePlaybook) return;
    for (const f of this.activePlaybook.fields) {
      if (f.type === 'context' && f.dependsOn === fieldId) {
        this.playbookFieldValues = { ...this.playbookFieldValues, [f.id]: '' };
        const { [f.id]: _, ...rest } = this.playbookContextEntities;
        this.playbookContextEntities = rest;
      }
    }
  }

  private loadContextFieldItems(uid: string, contextType: string): void {
    this.contextFieldItemSubs.forEach(s => s.unsubscribe());
    this.contextFieldItemSubs = [];
    this.contextFieldItems = [];

    if (!uid) return;

    // Map contextType to Firestore collection
    const collectionMap: Record<string, { path: string; nameField: string }> = {
      proposal: { path: `users/${uid}/proposals`, nameField: 'name' },
      task: { path: `users/${uid}/tasks`, nameField: 'name' },
      plan: { path: `users/${uid}/wireframes`, nameField: 'name' },
      note: { path: `users/${uid}/records`, nameField: 'title' },
    };

    const col = collectionMap[contextType];
    if (!col) return;

    const sub = this.afs.collection(col.path).valueChanges({ idField: 'id' }).subscribe((items: any[]) => {
      this.contextFieldItems = items.map(item => ({
        ...item,
        type: contextType,
        displayName: item[col.nameField] || `Untitled ${contextType}`
      }));
      this.cdr.markForCheck();
    });
    this.contextFieldItemSubs.push(sub);
  }

  openPlaybookPicker(): void {
    this.showPromptPopover = true;
    this.selectedPromptIndex = 0;
    this.openPromptPickerModal();
  }

  private loadContextItems(uid: string): void {
    this.contextItemSubscriptions.forEach(s => s.unsubscribe());
    this.contextItemSubscriptions = [];
    this.contextItems = [];

    if (!uid) return;

    const collections = [
      { path: `users/${uid}/wireframes`, type: 'plan', nameField: 'name' },
      { path: `users/${uid}/records`, type: 'note', nameField: 'title' },
      { path: `users/${uid}/proposals`, type: 'proposal', nameField: 'name' },
      { path: `users/${uid}/tasks`, type: 'task', nameField: 'name' },
      { path: `users/${uid}/tickets`, type: 'ticket', nameField: 'subject' },
      { path: `users/${uid}/apps`, type: 'app', nameField: 'name' },
      { path: `users/${uid}/media`, type: 'media', nameField: 'name' }
    ];

    const dataByType: Record<string, any[]> = {};

    for (const col of collections) {
      const sub = this.afs.collection(col.path).valueChanges({ idField: 'id' }).subscribe((items: any[]) => {
        dataByType[col.type] = items.map(item => ({
          ...item,
          type: col.type,
          displayName: item[col.nameField] || `Untitled ${col.type}`
        }));
        this.contextItems = Object.values(dataByType).flat();
        this.cdr.markForCheck();
      });
      this.contextItemSubscriptions.push(sub);
    }
  }

  toggleExpand(): void {
    this.expandToggled.emit(!this.isExpanded);
    // Focus the appropriate input after toggle
    setTimeout(() => {
      this.focusInput();
    }, 0);
  }

  toggleMicrophone(): void {
    this.microphoneToggled.emit();
  }

  sendMessage(): void {
    if (this.isCreditDepleted) {
      this.addCreditsRequested.emit();
      return;
    }

    if (this.canSendCurrentMessage && !this.loading) {
      this.messageSent.emit();
    }
  }

  onContextUsageClick(): void {
    this.openSelectorModal('contextUsage');
  }

  onForceCompactionClick(): void {
    if (!this.canForceCompaction) {
      return;
    }

    this.forceCompactionRequested.emit();
  }

  private formatCreditCount(value: number): string {
    return new Intl.NumberFormat('en-AU', {
      maximumFractionDigits: value >= 100 ? 0 : 2
    }).format(value);
  }

  async stopCurrentThread(): Promise<void> {
    if (!this.activeThreadId) {
      return;
    }

    try {
      await this.afs.collection('threads').doc(this.activeThreadId).update({
        status: 'stopped',
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error stopping thread:', error);
      this.toastService.showError('Failed to stop the current task.');
    }
  }

  private hydrateImageAttachments(items: ChatImageAttachment[] | null | undefined): void {
    this.composerAttachedImageItems = (items || []).map(item => ({ ...item }));
    this.cdr.markForCheck();
  }

  private hydrateFileAttachments(items: ChatFileAttachment[] | null | undefined): void {
    this.composerAttachedFileItems = (items || []).map(item => ({ ...item }));
    this.cdr.markForCheck();
  }

  private async attachImageFiles(files: File[]): Promise<void> {
    const supportedFiles = files.filter(file => this.isSupportedImageFile(file));

    if (supportedFiles.length === 0) {
      this.toastService.showError('Only PNG, JPEG, WebP, and GIF images can be attached.');
      return;
    }

    const remainingSlots = Math.max(0, MAX_IMAGE_ATTACHMENTS - this.composerAttachedImageItems.length);
    if (remainingSlots === 0) {
      this.toastService.showError(`You can attach up to ${MAX_IMAGE_ATTACHMENTS} images per message.`);
      return;
    }

    const filesToAttach = supportedFiles.slice(0, remainingSlots);
    if (supportedFiles.length > filesToAttach.length) {
      this.toastService.showError(`Only the first ${remainingSlots} image${remainingSlots === 1 ? '' : 's'} were attached.`);
    }

    for (const file of filesToAttach) {
      if (file.size > MAX_IMAGE_ATTACHMENT_BYTES) {
        this.toastService.showError(`${file.name || 'Image'} is larger than 10 MB and cannot be attached.`);
        continue;
      }

      const attachment = this.createPendingImageAttachment(file);
      this.composerAttachedImageItems = [...this.composerAttachedImageItems, attachment];
      this.cdr.markForCheck();
      void this.uploadImageAttachment(file, attachment);
    }
  }

  private async attachFiles(files: File[]): Promise<void> {
    for (const file of files.filter(Boolean)) {
      if (file.size > MAX_CONTEXT_FILE_BYTES) {
        this.toastService.showError(`${file.name || 'File'} is larger than 25 MB and cannot be attached.`);
        continue;
      }

      const attachment = this.createPendingFileAttachment(file);
      this.composerAttachedFileItems = [...this.composerAttachedFileItems, attachment];
      this.cdr.markForCheck();
      void this.uploadFileAttachment(file, attachment);
    }
  }

  private createPendingImageAttachment(file: File): ChatImageAttachment {
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      name: file.name?.trim() || `image-${Date.now()}`,
      mimeType: file.type,
      sizeBytes: file.size,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading',
      uploadProgress: 0,
      url: ''
    };
  }

  private createPendingFileAttachment(file: File): ChatFileAttachment {
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      name: file.name?.trim() || `file-${Date.now()}`,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      status: 'uploading',
      uploadProgress: 0,
      url: ''
    };
  }

  private async uploadImageAttachment(file: File, attachment: ChatImageAttachment): Promise<void> {
    const storagePath = this.buildImageAttachmentStoragePath(file, attachment.id);
    console.log('[chat-attachments] starting shared image upload', {
      id: attachment.id,
      name: attachment.name,
      storagePath,
      bucket: (this.afStorage.storage.app.options as { storageBucket?: string }).storageBucket
    });

    try {
      const dimensions = await this.readImageDimensions(attachment.previewUrl || '');
      const uploadTask = this.afStorage.storage.ref(storagePath).put(file, {
        contentType: file.type,
        cacheControl: 'public,max-age=31536000'
      });

      uploadTask.on('state_changed', snapshot => {
        const uploadProgress = snapshot.totalBytes > 0
          ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          : 0;
        this.updateImageAttachment(attachment.id, { uploadProgress });
      });

      const snapshot = await uploadTask;
      const downloadUrl = await snapshot.ref.getDownloadURL();

      this.updateImageAttachment(attachment.id, {
        url: downloadUrl,
        storagePath,
        status: 'ready',
        uploadProgress: 100,
        ...(dimensions.width ? { width: dimensions.width } : {}),
        ...(dimensions.height ? { height: dimensions.height } : {})
      });
    } catch (error) {
      console.error('Failed to upload shared image attachment:', error);
      this.updateImageAttachment(attachment.id, {
        status: 'error',
        uploadProgress: 0,
        errorMessage: 'Upload failed'
      });
      this.toastService.showError(`Failed to upload ${attachment.name || 'image'}.`);
    }
  }

  private async uploadFileAttachment(file: File, attachment: ChatFileAttachment): Promise<void> {
    const storagePath = this.buildFileAttachmentStoragePath(file, attachment.id);
    console.log('[chat-attachments] starting shared file upload', {
      id: attachment.id,
      name: attachment.name,
      storagePath,
      bucket: (this.afStorage.storage.app.options as { storageBucket?: string }).storageBucket
    });

    try {
      const uploadTask = this.afStorage.storage.ref(storagePath).put(file, {
        contentType: file.type || 'application/octet-stream',
        cacheControl: 'public,max-age=31536000'
      });

      uploadTask.on('state_changed', snapshot => {
        const uploadProgress = snapshot.totalBytes > 0
          ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          : 0;
        this.updateFileAttachment(attachment.id, { uploadProgress });
      });

      const snapshot = await uploadTask;
      const downloadUrl = await snapshot.ref.getDownloadURL();

      this.updateFileAttachment(attachment.id, {
        url: downloadUrl,
        storagePath,
        status: 'ready',
        uploadProgress: 100
      });
    } catch (error) {
      console.error('Failed to upload shared file attachment:', error);
      this.updateFileAttachment(attachment.id, {
        status: 'error',
        uploadProgress: 0,
        errorMessage: 'Upload failed'
      });
      this.toastService.showError(`Failed to upload ${attachment.name || 'file'}.`);
    }
  }

  private updateImageAttachment(id: string, patch: Partial<ChatImageAttachment>): void {
    this.composerAttachedImageItems = this.composerAttachedImageItems.map(item => item.id === id ? { ...item, ...patch } : item);
    this.cdr.markForCheck();
  }

  private updateFileAttachment(id: string, patch: Partial<ChatFileAttachment>): void {
    this.composerAttachedFileItems = this.composerAttachedFileItems.map(item => item.id === id ? { ...item, ...patch } : item);
    this.cdr.markForCheck();
  }

  private removeImageAttachment(id: string): void {
    const attachment = this.composerAttachedImageItems.find(item => item.id === id);
    if (attachment?.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(attachment.previewUrl);
    }

    this.composerAttachedImageItems = this.composerAttachedImageItems.filter(item => item.id !== id);
    this.cdr.markForCheck();
  }

  private removeFileAttachment(id: string): void {
    this.composerAttachedFileItems = this.composerAttachedFileItems.filter(item => item.id !== id);
    this.cdr.markForCheck();
  }

  private isSupportedImageFile(file: File): boolean {
    return !!file.type && SUPPORTED_IMAGE_MIME_TYPES.has(file.type.toLowerCase());
  }

  private buildImageAttachmentStoragePath(file: File, attachmentId: string): string {
    const ownerId = this.contextUid || this.authService.aid || 'anonymous';
    const threadSegment = this.currentThreadId || 'draft';
    const safeName = (file.name || 'image')
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/-+/g, '-');

    return `users/${ownerId}/agent-chat-images/${threadSegment}/${attachmentId}-${safeName}`;
  }

  private buildFileAttachmentStoragePath(file: File, attachmentId: string): string {
    const ownerId = this.contextUid || this.authService.aid || 'anonymous';
    const threadSegment = this.currentThreadId || 'draft';
    const safeName = (file.name || 'file')
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/-+/g, '-');

    return `users/${ownerId}/agent-chat-files/${threadSegment}/${attachmentId}-${safeName}`;
  }

  private readImageDimensions(sourceUrl: string): Promise<{ width?: number; height?: number }> {
    if (!sourceUrl) {
      return Promise.resolve({});
    }

    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => resolve({});
      image.src = sourceUrl;
    });
  }

  focusInput(): void {
    setTimeout(() => {
      if (this.chatTextarea) {
        this.chatTextarea.nativeElement.focus();
      }
    }, 0);
  }

  private focusInputAt(position: number): void {
    setTimeout(() => {
      const textarea = this.chatTextarea?.nativeElement;
      if (!textarea) {
        return;
      }

      textarea.focus();
      textarea.setSelectionRange(position, position);
      this.autoResizeTextarea();
    }, 0);
  }

  getCursorPosition(): number {
    return this.chatTextarea?.nativeElement.selectionStart ?? this.newMessage.length;
  }

  private queueTextareaResize(): void {
    setTimeout(() => this.autoResizeTextarea(), 0);
  }

  private autoResizeTextarea(): void {
    const textarea = this.chatTextarea?.nativeElement;

    if (!textarea) {
      return;
    }

    textarea.style.setProperty('height', '0px', 'important');
    textarea.style.setProperty('max-height', `${this.maxComposerHeight}px`, 'important');
    const nextHeight = Math.min(textarea.scrollHeight, this.maxComposerHeight);
    textarea.style.setProperty('height', `${nextHeight}px`, 'important');
    textarea.style.setProperty('overflow-y', textarea.scrollHeight > this.maxComposerHeight ? 'auto' : 'hidden', 'important');
  }

  private getAgentLabel(): string {
    return getAgentName(this.agent);
  }

  /**
   * Check if context prompts should be shown
   * Only show when messageCount is 0, input is empty, there are featured prompts, and no attached context
   */
  get hasInputText(): boolean {
    return !!this.newMessage && this.newMessage.trim().length > 0;
  }

  get canShowContextPrompts(): boolean {
    return this.messageCount === 0 &&
           this.contextPrompts.length > 0 &&
           !this.loading &&
           !this.activePlaybook &&
           !this.hasAttachedContext;
  }

  get shouldShowSuggestionsAbove(): boolean {
    return this.canShowContextPrompts && !this.hasInputText && this.suggestionsPlacement === 'above';
  }

  get shouldShowSuggestionsBelow(): boolean {
    return this.canShowContextPrompts && this.suggestionsPlacement === 'below';
  }

  /**
   * Handle clicking on a context prompt suggestion (playbook)
   */
  onContextPromptClick(playbook: PlaybookEntry): void {
    // Clear any hover state
    this.onSuggestionLeave();
    
    // Activate the playbook inline
    this.activatePlaybook(playbook, 'internal');
    this.cdr.markForCheck();
  }

  /**
   * Handle hovering over a suggestion item
   */
  onSuggestionHover(playbook: PlaybookEntry, event: MouseEvent): void {
    // Clear any existing timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    // Set up a delay before showing the popover
    this.hoverTimeout = setTimeout(() => {
      this.hoveredPrompt = playbook;
      
      // Calculate popover position to match the item width and position
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      const scrollContainer = target.closest('.suggestions-container');
      const containerRect = scrollContainer?.getBoundingClientRect();
      
      if (containerRect) {
        // Position completely above the suggestion item, but use full container width
        // Popover height: 56px content + 16px padding + 2px border + 6px arrow = 80px total
        const popoverHeight = 80;
        
        // Account for the context-suggestions container margin-bottom (12px) and suggestions-container padding
        const containerOffset = 16; // margin + padding adjustments
        
        this.popoverPosition = {
          x: 0, // Start at container left edge
          y: rect.top - containerRect.top - popoverHeight - containerOffset, // Position above with proper clearance
          width: containerRect.width // Use full container width
        };
      }
    }, 1000); // 1 second delay
  }

  /**
   * Handle leaving a suggestion item
   */
  onSuggestionLeave(): void {
    // Clear timeout if still pending
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    
    // Hide popover
    this.hoveredPrompt = null;
  }
}
